import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { selectCheckout, setMode, setAddress, setCustomer, setScheduledAt, setChannel, setCustomerNote, setCartOption, clearCartOptions, bindRestaurant, setRedemptionCode as setRedemptionCodeAction } from './checkoutSlice';
import { selectLinesByRestaurant, selectCartSubtotal, clearRestaurantCart, removeLine, updateLineUnitPrice, setBuffetBudget, setMode as setCartMode } from '@/features/cart/cartSlice';
import { diagnoseCartLines, remainingPeriodsToday, blockedLineNames, type BlockedCartLine } from '@/features/cart/cartAvailability';
import UnavailableItemsModal from '@/features/cart/UnavailableItemsModal';
import { clearTableSession, selectIsSharedTableCart } from '@/features/tableSession/tableSessionSlice';
import { showErrorToast } from '@/features/ui/uiSlice';
import SharedTableCheckout from './SharedTableCheckout';
import { usePlaceOrderMutation, sentRoundsParam } from './ordersApi';
import Modal from '@/components/ui/Modal';
import ServiceChoiceButtons from './ServiceChoiceButtons';
import { useServiceChoiceQuestion } from './serviceChoice';
import { useRestaurant } from '@/features/restaurant/useRestaurant';
import { useGetMenuQuery } from '@/app/api/resourceApi';
import { selectTableSession } from '@/features/tableSession/tableSessionSlice';
import { useTableOrderingLock } from '@/features/tableSession/useTableOrderingLock';
import { repriceCartLines } from '@/features/cart/repriceCartLines';
import {
  useFetchDeliveryQuoteMutation,
  useGetDeliveryConfigQuery,
  type Destination,
} from './deliveryApi';
import { useAllowedModes } from './useAllowedModes';
import { DeliveryStep } from './DeliveryStep';
import { quoteFromResponse, type QuoteStatus } from './DeliveryQuotePanel';
import {
  selectAddresses,
  selectCurrentUser,
  selectDefaultAddress,
  selectIsAuthenticated,
} from '@/features/user/selectors';
import { loyaltyApi, useGetLoyaltyBalanceQuery, useGetLoyaltyCardQuery, useGetLoyaltyProgramQuery } from '@/features/loyalty/loyaltyApi';
import { earnEstimate, guestNudge, isUsableOnline, leavesTooLittleOnline, totalAfterRedemption } from '@/features/loyalty/format';
import GuestPointsBanner from '@/features/loyalty/components/GuestPointsBanner';
import { useSessionFlag } from '@/features/loyalty/useGuestLoyalty';
import OnlineTooSmallNotice from '@/features/loyalty/components/OnlineTooSmallNotice';
import { addAddress, removeAddress, setDefaultAddress, updateMeUser } from '@/features/user/userSlice';
import { openAccountModal } from '@/features/user/accountModalSlice';
import Price from '@/components/ui/Price';
import UpsellBanner from '@/components/ui/UpsellBanner';
import NoteField from '@/components/ui/NoteField';
import { todayCheckoutSlots, dayCheckoutSlots, filterSlotsByCartAvailability, firstSlotFor, asapEnabledFor } from './checkoutSlots';
import { composeScheduledIso } from './composeScheduledIso';
import { restaurantTz, todayIsoInTz, localParts, dateFromLocal } from '@/lib/hours';
import { dayLabelForOffset, weekdayNameForOffset } from './dayLabel';
import { normalizePreorderMaxDays } from '@/types/restaurant';
import { selectPromotion, nextPromotionHint, eligibleDiscountSubtotal } from './selectPromotion';
import { minOrderCentsForOrder } from './minOrder';
import { quoteCityLabel, servedCityNames } from './servedCities';
import PaymentElementSection from './PaymentElementSection';
import StancerRedirectSection from './StancerRedirectSection';
import AccountInvite from './AccountInvite';
import CartOptionsSection from './CartOptionsSection';
import LoyaltyRedemption, { type LoyaltySelection } from './LoyaltyRedemption';
import {
  StepCard,
  StepRail,
  scrollToStep,
  STEP_SCROLL_MARGIN,
  type CheckoutStep,
} from './CheckoutSteps';
import {
  cartOptionsTotalCents,
  groupsForAmount,
  groupsForMode,
  nextCartOptionHint,
  selectionsForGroups,
  unansweredRequiredGroups,
} from './cartOptions';
import CancellationPolicyNotice from '@/features/restaurant/CancellationPolicyNotice';
import CartLineItem from '@/features/cart/CartLineItem';
import TurnstileWidget, { type TurnstileWidgetHandle } from '@/features/user/AccountModal/TurnstileWidget';
import CaptchaHelp, { type CaptchaHint } from './CaptchaHelp';
import { newCaptchaSession, reportCaptchaDiagnostic, reportSubmitErrorDiagnostic, type CaptchaDiagnosticState } from './captchaDiagnostic';
import { CUSTOMER_NAME_MAX, CUSTOMER_PHONE_MAX, isValidEmail } from './contactRules';
import './checkout.css';
import { getPublicCheckoutConfig, type ActiveProvider } from './stripeClient';
import { isOnlineAmountTooSmall, isSlotExpiredError, isTotalMismatch, orderErrorMessage, redemptionErrorMessage, rejectedContactField, rejectedFieldKeys, unavailableProductIds, type ContactField } from './orderErrors';
import type { PaymentChannel, ServiceChoice } from '@/types/order';
import { catalogName, useCatalogText } from '@/lib/localizeCatalog';

// A quote belongs to the full geocoded destination, never just to a place label.
// In particular, a saved address with missing/invalid coordinates cannot reuse it.
function deliveryDestinationKey(restaurantUuid: string, address: Partial<Destination>): string | null {
  if (!address.place_id || !Number.isFinite(address.lat) || !Number.isFinite(address.lng)) return null;
  return JSON.stringify([restaurantUuid, address.place_id, address.lat, address.lng, address.formatted_address ?? '']);
}

// How often the pickup/delivery slot list is recomputed against the wall clock
// (SC-568). Short enough that a slot never sits expired in the picker for long,
// long enough to stay invisible; the submit path re-checks anyway.
const SLOT_REFRESH_MS = 30_000;
/** SC-1500 — valeur de l'option « Dès que possible » du sélecteur d'horaire. */
const ASAP_OPTION = 'asap';

export default function CheckoutPage() {
  // SC-169: in shared dine-in (ordering mode, flag on) the cart lives server-side
  // and there's no per-customer payment/contact step — render a dedicated
  // review-and-send screen. The full takeaway/delivery/dine-in checkout below
  // (RegularCheckout) is left byte-for-byte unchanged for every other case.
  const { restaurantUuid = '', tableToken } = useParams<{
    restaurantUuid: string;
    tableToken?: string;
  }>();
  const tableSession = useAppSelector(selectTableSession);
  const isShared = useAppSelector(selectIsSharedTableCart);
  const sharedActive =
    isShared &&
    tableSession !== null &&
    tableSession.restaurantUuid === restaurantUuid &&
    tableSession.tableToken === tableToken;
  if (sharedActive) return <SharedTableCheckout />;
  return <RegularCheckout />;
}

function RegularCheckout() {
  const { restaurantUuid = '', tableToken: routeTableToken } = useParams<{
    restaurantUuid: string;
    tableToken?: string;
  }>();
  const { t, i18n } = useTranslation();
  const ct = useCatalogText();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const checkout = useAppSelector(selectCheckout);
  const [activeProvider, setActiveProvider] = useState<ActiveProvider>('stripe');
  // SC-574: when this config can't be read (or comes back without the Stripe
  // publishable key) `getStripePromise` rejects, <Elements> never mounts and the
  // card block rendered as a blank rectangle — no button, no message, nothing to
  // retry. Remember the failure so the block can say so and point at "pay on site".
  const [checkoutConfigFailed, setCheckoutConfigFailed] = useState(false);

  useEffect(() => {
    if (!restaurantUuid) return;
    getPublicCheckoutConfig(restaurantUuid)
      .then((cfg) => {
        setActiveProvider(cfg.active_provider);
        setCheckoutConfigFailed(cfg.active_provider === 'stripe' && !cfg.stripe_publishable_key);
      })
      .catch(() => setCheckoutConfigFailed(true));
  }, [restaurantUuid]);
  const lines = useAppSelector(selectLinesByRestaurant(restaurantUuid));
  const subtotal = useAppSelector(selectCartSubtotal(restaurantUuid));
  const [placeOrder, { isLoading }] = usePlaceOrderMutation();
  const isAuthed = useAppSelector(selectIsAuthenticated);
  const currentUser = useAppSelector(selectCurrentUser);
  // L9-2: loyalty redemption code. Gated on the same balance query the account/
  // confirmation pages already use — an anonymous guest or a restaurant with no
  // active loyalty program never sees the section.
  const { data: loyalty } = useGetLoyaltyBalanceQuery(restaurantUuid, {
    skip: !isAuthed || !restaurantUuid,
  });
  // Fidélité v2: the code lives in the checkout slice so the cart drawer can
  // preselect a wallet coupon; it is still what leaves as `redemption_code`.
  const redemptionCode = checkout.redemptionCode;
  const setRedemptionCode = (code: string) => dispatch(setRedemptionCodeAction(code));
  const [redemptionError, setRedemptionError] = useState<string | null>(null);
  // SC-1091 B1 — the server refused the code AFTER « Payer »: the step must be
  // reopened and marked, or the button looks dead (the step is `hidden`).
  // `small` = refused because the advantage leaves under 0,50 € to pay online: it
  // lifts on its own when the customer switches to on-site (see the effect below).
  const [loyaltyRejection, setLoyaltyRejection] = useState<'code' | 'small' | null>(null);
  const loyaltyRejected = loyaltyRejection !== null;
  const handleRedemptionError = (message: string | null) => {
    setRedemptionError(message);
    if (message === null) setLoyaltyRejection(null);
  };
  useEffect(() => {
    if (loyaltyRejection === 'small') {
      setLoyaltyRejection(null);
      setRedemptionError(null);
    }
    // Only a change of payment channel lifts it — read the current rejection then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkout.payment.channel]);
  // The hub answered « disabled » to a typed code: the program is off — the whole step
  // goes away (it is not a bad code), and the balance query is re-read.
  const [programDisabled, setProgramDisabled] = useState(false);
  const handleProgramDisabled = () => {
    setProgramDisabled(true);
    setRedemptionCode('');
    dispatch(loyaltyApi.util.invalidateTags(['LoyaltyBalance', 'LoyaltyRewards', 'LoyaltyCard']));
  };
  // What the loyalty step selected, priced — feeds the estimate lines outside it.
  const [loyaltySelection, setLoyaltySelection] = useState<LoyaltySelection | null>(null);
  // SC-1628 — read even when the programme is off: the coupons already held are
  // still honoured by the order (SC-440), so the step stays for them.
  const { data: loyaltyCard } = useGetLoyaltyCardQuery(restaurantUuid, {
    skip: !isAuthed || !restaurantUuid || !loyalty,
  });
  const defaultAddr = useAppSelector(selectDefaultAddress);
  const savedAddresses = useAppSelector(selectAddresses);
  const location = useLocation();
  const { identity, config } = useRestaurant(restaurantUuid);
  // DF-0330 — every slot/schedule computation below must read the wall clock
  // in the RESTAURANT's own timezone, never the customer's browser (a DOM
  // restaurant's `country` still reads "FR" while its hours run on Cayenne).
  const tz = restaurantTz(identity ?? {});

  // Dine-in detection: an active table session activates the slim checkout variant.
  const tableSession = useAppSelector(selectTableSession);
  const isDineIn =
    tableSession !== null &&
    tableSession.restaurantUuid === restaurantUuid &&
    tableSession.tableToken === routeTableToken;
  // SC-218: in a buffet dine-in session, included products price their base at 0
  // — recompute line prices with that base so the submitted total matches the
  // server quote (else total_mismatch on the binding non-shared dine-in path).
  const isBuffetDineIn = isDineIn && tableSession?.mode === 'buffet';
  // SC-1091 — a GUEST is reminded that logging in earns points: the public program
  // (no token), never on a table order (no loyalty there); silent when it fails.
  const { data: guestProgram } = useGetLoyaltyProgramQuery(restaurantUuid, {
    skip: isAuthed || isDineIn || !restaurantUuid,
  });
  const [guestBannerDismissed, dismissGuestBanner] = useSessionFlag(`guestNudgeDismissed:${restaurantUuid}`);
  const orderingLocked = useTableOrderingLock();
  // DF-0586 — the table chooses « tout en même temps » / « service par
  // service » on its LAST step only, when the cart spans several services.
  const [serviceChoiceOpen, setServiceChoiceOpen] = useState(false);
  const serviceQuestion = useServiceChoiceQuestion(
    restaurantUuid,
    isDineIn ? lines.map((line) => line.itemId) : [],
  );

  // Cloudflare Turnstile (managed → invisible for most visitors). Only online
  // (non-dine_in) orders are captcha-gated server-side; dine_in is exempt
  // (table-session JWT). Without a site key (dev/CI) the backend fails open.
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY ?? '';
  const needCaptcha = !isDineIn && !!recaptchaSiteKey;
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  // SC-574: the widget failed to produce a token at all (script blocked, bad
  // key). Distinct from "no token yet": waiting is worth a "hold on" message,
  // but a broken widget must NOT lock the customer out client-side — the server
  // fails open where no secret is configured, so let the POST decide.
  const [captchaBroken, setCaptchaBroken] = useState(false);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  // Turnstile tokens are single-use: reset after every place-order attempt so a
  // retry (e.g. failed payment) gets a fresh token from the managed widget.
  const resetCaptcha = () => { turnstileRef.current?.reset(); setRecaptchaToken(null); };
  // DF-0419 — the widget can also leave the customer with no token and no
  // error: the interactive box shown and not ticked, Turnstile's own timeout,
  // an unsupported browser, or nothing answering at all. None of these unlock
  // the button (the server would refuse the POST anyway where a secret is
  // set); they put a lasting explanation under the widget instead.
  const [captchaIssue, setCaptchaIssue] = useState<'interactive' | 'stuck' | null>(null);
  // A click refused for want of a token: from then on the help stays visible,
  // even while the widget is still within its normal couple of seconds.
  const [captchaBlockedClick, setCaptchaBlockedClick] = useState(false);
  const captchaSessionRef = useRef(newCaptchaSession());
  const captchaMountedAtRef = useRef<number | null>(null);
  const captchaHint: CaptchaHint = recaptchaToken
    ? 'none'
    : captchaBroken || captchaIssue === 'stuck'
      ? 'stuck'
      : captchaIssue === 'interactive'
        ? 'interactive'
        : captchaBlockedClick
          ? 'waiting'
          : 'none';
  const reportCaptcha = (state: CaptchaDiagnosticState) =>
    reportCaptchaDiagnostic({
      state,
      restaurantUuid,
      session: captchaSessionRef.current,
      elapsedMs: captchaMountedAtRef.current == null ? 0 : Date.now() - captchaMountedAtRef.current,
      hint: captchaHint,
    });
  // The captcha is `validateBeforePlace`'s last check, so its message is the
  // refusal only when everything else on the form was fine.
  const noteCaptchaRefusal = (validationError: string) => {
    if (validationError !== t('checkout.errors.recaptchaRequired')) return;
    reportCaptcha('blocked_click');
    setCaptchaBlockedClick(true);
  };
  const retryCaptcha = () => {
    reportCaptcha('retry');
    setCaptchaIssue(null);
    setCaptchaBroken(false);
    resetCaptcha();
  };

  // Payment channel availability: derived from config (loaded via useRestaurant).
  // Online payment isn't launched yet (SC-198): a global launch flag forces the
  // "Payer en ligne" channel off everywhere regardless of the per-restaurant
  // payment_online_enabled config. Flip VITE_ONLINE_PAYMENT_LAUNCHED=true at launch.
  const onlinePaymentLaunched = import.meta.env.VITE_ONLINE_PAYMENT_LAUNCHED === 'true';
  const paymentOnlineEnabled = onlinePaymentLaunched && (config?.paymentOnlineEnabled ?? true);
  const paymentOnsiteEnabled = config?.paymentOnsiteEnabled ?? true;
  const paymentOnlineDescription = config?.paymentOnlineDescription ?? '';
  const paymentOnsiteDescription = config?.paymentOnsiteDescription ?? '';

  // CartDrawer keeps the menu query subscribed for the whole session, so the
  // cache outlives any back-office price change. Refetch on mount and compare
  // each cart line's frozen unitPrice against the live menu — sync the cart
  // and surface a banner so the customer notices before paying.
  const { data: menu, isFetching: isMenuFetching, refetch: refetchMenu } = useGetMenuQuery(
    restaurantUuid,
    { skip: !restaurantUuid, refetchOnMountOrArgChange: true },
  );
  const [priceChangedNames, setPriceChangedNames] = useState<string[]>([]);
  useEffect(() => {
    // SC-524: same reconciliation the cart drawer runs on open, so the two
    // surfaces can never quote different prices for the same line.
    const changed = repriceCartLines(
      lines,
      menu,
      isBuffetDineIn,
      isDineIn ? 'dine_in' : checkout.mode,
    );
    for (const line of changed) {
      dispatch(updateLineUnitPrice({ restaurantUuid, lineId: line.lineId, unitPrice: line.unitPrice }));
    }
    setPriceChangedNames(changed.map((line) => line.name));
    // Re-running on `lines` reference would loop after every dispatch — gate
    // on the menu snapshot instead and read lines fresh on each fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu, restaurantUuid, dispatch, isBuffetDineIn, isDineIn, checkout.mode]);

  const enabledChannels = useMemo<PaymentChannel[]>(() => {
    const channels: PaymentChannel[] = [];
    if (paymentOnlineEnabled) channels.push('online');
    if (paymentOnsiteEnabled) channels.push('on_site');
    return channels;
  }, [paymentOnlineEnabled, paymentOnsiteEnabled]);

  const paymentsEmpty = enabledChannels.length === 0;
  const paymentsBlocked = paymentsEmpty;

  // Un compte sans téléphone (inscription Google) : le numéro se tape dans le
  // champ de l'étape « Coordonnées », comme pour tout le monde, et part aussi sur
  // le profil (`savePhoneToProfile`). Il y avait une seconde invite, montée à la
  // place de la barre d'action, qui redemandait le même numéro sous le champ.
  const phoneMissing = !!currentUser && currentUser.phone === '';

  const [submitAttempted, setSubmitAttempted] = useState(false);
  // DF-0573 — the contact value sale_api refused: the field stays red and the
  // order is not re-sent while the customer has not changed it, even where the
  // page's own rule would have let it through.
  const [refusedContact, setRefusedContact] = useState<{ field: ContactField; value: string } | null>(null);
  // DF-0573 — what the last refused send was told. Turnstile renews its
  // single-use token for a second or two after a refusal; a click in that gap
  // used to read « la vérification anti-robot n'est pas terminée », and the
  // customer blamed the anti-robot for an e-mail the server had refused.
  const [lastSendFailure, setLastSendFailure] = useState<string | null>(null);
  // Les étapes sur lesquelles « Suivant » a déjà refusé d'avancer. Une étape ne
  // rougit que si le client a buté DESSUS (ou a tenté d'envoyer) : un « Suivant »
  // raté sur « Coordonnées » rougissait aussi la question de fin de parcours,
  // que le client n'avait pas encore vue.
  const [attemptedSteps, setAttemptedSteps] = useState<string[]>([]);
  const attempted = (id: string) => submitAttempted || attemptedSteps.includes(id);
  const addressSectionRef = useRef<HTMLElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  // SC-570: same derivation as the cart drawer — "delivery" needs both
  // resource_api's enable_livraison and sale_api's DeliveryConfig.enabled.
  const { modes: allowedModes, resolved: modesResolved } = useAllowedModes(restaurantUuid);

  // Mutation-backed delivery quote. The latest response feeds both the
  // delivery fee and the DeliveryStep's QuotePanel.
  const [triggerDeliveryQuote, { data: quoteData, isLoading: quoteLoading, isError: quoteIsError, originalArgs: quoteArgs }] =
    useFetchDeliveryQuoteMutation();
  const destinationKey = checkout.mode === 'delivery'
    ? deliveryDestinationKey(restaurantUuid, checkout.address)
    : null;
  const currentDestinationKeyRef = useRef(destinationKey);
  currentDestinationKeyRef.current = destinationKey;
  const [refusedDestination, setRefusedDestination] = useState<{ key: string; city?: string } | null>(null);
  const quoteMatchesAddress = destinationKey !== null && quoteArgs !== undefined &&
    deliveryDestinationKey(quoteArgs.restaurantUuid, quoteArgs.destination) === destinationKey;
  const quoteStatus: QuoteStatus = destinationKey && refusedDestination?.key === destinationKey
    ? { status: 'city_not_served', city: refusedDestination.city }
    : quoteMatchesAddress ? quoteFromResponse(quoteData, quoteLoading, quoteIsError) : { status: 'idle' };
  // SC-1155 : le rappel différé de DeliveryStep (300 ms) garde les props d'un
  // ancien rendu ; il lit donc le statut courant par cette ref, rafraîchie à chaque rendu.
  const quoteStatusRef = useRef(quoteStatus.status);
  quoteStatusRef.current = quoteStatus.status;
  // SC-1155 : même requête que useAllowedModes (cache RTK partagé, pas de 2e appel).
  const { currentData: deliveryConfig } = useGetDeliveryConfigQuery(restaurantUuid, {
    skip: !restaurantUuid,
  });
  const servedCities =
    deliveryConfig?.zone_mode === 'CITY' ? servedCityNames(deliveryConfig.cities) : [];

  // SC-1155 : le bloc du devis atterrit sous la barre d'action collante ; on le
  // ramène à l'écran quand un devis se règle APRÈS un geste du client (choix
  // d'adresse, adresse enregistrée, réessai) — jamais au montage ni à la
  // restauration d'un devis (rechargement, retour d'une autre étape, bascule
  // emporter/livraison).
  const quotePanelRef = useRef<HTMLDivElement>(null);
  const scrollToQuoteRef = useRef(false);
  useEffect(() => {
    const st = quoteStatus.status;
    if (!scrollToQuoteRef.current || st === 'idle' || st === 'loading') return;
    scrollToQuoteRef.current = false;
    const el = quotePanelRef.current;
    if (!el) return;
    const barTop =
      document.querySelector('[data-testid="checkout-action-bar"]')?.getBoundingClientRect().top ??
      window.innerHeight;
    const rect = el.getBoundingClientRect();
    if (rect.top >= 0 && rect.bottom <= barTop - 12) return;
    el.style.scrollMarginBottom = `${Math.max(0, window.innerHeight - barTop) + 12}px`;
    el.style.scrollMarginTop = STEP_SCROLL_MARGIN;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [quoteStatus.status]);

  const lastQuotedDestinationKeyRef = useRef<string | null>(null);
  function handleTriggerQuote(destination: Destination) {
    const key = deliveryDestinationKey(restaurantUuid, destination);
    if (!key) return;
    lastQuotedDestinationKeyRef.current = key;
    setRefusedDestination(null);
    triggerDeliveryQuote({
      restaurantUuid,
      destination: {
        place_id: destination.place_id,
        lat: destination.lat,
        lng: destination.lng,
        formatted_address: destination.formatted_address ?? '',
      },
    });
  }

  // Saved/restored addresses use the same server check as a fresh Places pick.
  // Reset on an incomplete address or mode change so returning cannot reuse an
  // older quote; address instructions alone do not change the destination key.
  useEffect(() => {
    if (!destinationKey) {
      lastQuotedDestinationKeyRef.current = null;
      return;
    }
    if (lastQuotedDestinationKeyRef.current === destinationKey) return;
    handleTriggerQuote(checkout.address as Destination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationKey]);

  // The automatic effect already quotes the pick. Ignore duplicate debounce
  // callbacks and callbacks belonging to an address that has since changed.
  function handleDeliveryStepQuote(destination: Destination) {
    const key = deliveryDestinationKey(restaurantUuid, destination);
    if (key !== currentDestinationKeyRef.current) return;
    if (lastQuotedDestinationKeyRef.current === key && quoteStatusRef.current !== 'error') return;
    handleTriggerQuote(destination);
  }

  function handleRetryQuote() {
    scrollToQuoteRef.current = true;
    if (destinationKey) handleTriggerQuote(checkout.address as Destination);
  }

  // SC-568: "now" has to keep moving. Frozen at mount, the earliest slot
  // (now + prep) quietly expired while the customer filled the form and the
  // order came back 409 scheduled_at_in_past at submit — with the dead slot
  // still sitting in the picker, so re-picking it failed again. Re-snapshot
  // every SLOT_REFRESH_MS so expired slots leave the list on their own.
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), SLOT_REFRESH_MS);
    return () => clearInterval(id);
  }, []);
  const slotPrepMinutes = checkout.mode === 'delivery'
    ? config?.prepTimeLivraisonMin
    : config?.prepTimeEmporterMin;
  // SC-1465 — premier créneau du mode : automatique (ouverture + préparation)
  // ou fixé à la main par jour et par service dans le back-office.
  const slotFirstSlot = firstSlotFor(identity, checkout.mode);
  // SC-1090 pre-commande J+N: day toggle, offset 0 (today) .. preorderMaxDays.
  // Missing/invalid horizon reads as 1 (Aujourd'hui/Demain only) — same as
  // the J+1 behaviour this generalizes.
  const preorderMaxDays = normalizePreorderMaxDays(identity?.preorderMaxDays);
  const [selectedDay, setSelectedDay] = useState<number>(0);
  // DF-0273 — « Aujourd'hui » / « Demain » nus ne disent pas quel jour on
  // achète, et apres minuit « demain » ne veut plus rien dire : chaque onglet
  // porte sa date. Elle suit `now`, qui se re-prend toutes les SLOT_REFRESH_MS,
  // donc elle bascule d'elle-meme a minuit.
  const todayDayLabel = dayLabelForOffset(now, 0, i18n.language, identity);
  const tomorrowDayLabel = dayLabelForOffset(now, 1, i18n.language, identity);

  // Per-offset slot data, index 0..preorderMaxDays. `daySlots` is what the
  // opening hours allow; `slots` is what the cart also allows. SC-574: keeping
  // both apart lets the empty-list message name the real cause — "closed
  // today" and "your cart's items don't fit any remaining service" are
  // different problems with different fixes. Every day starts at opening +
  // prep time (SC-1435); only today also counts prep from `now`.
  const dayData = useMemo<{ daySlots: string[]; slots: string[] }[]>(() => {
    if (!identity?.hours) return [];
    const out: { daySlots: string[]; slots: string[] }[] = [];
    for (let offset = 0; offset <= preorderMaxDays; offset++) {
      const openSlots = slotPrepMinutes == null
        ? []
        : offset === 0
          ? todayCheckoutSlots(identity.hours, now, slotPrepMinutes, undefined, identity.closures, tz, slotFirstSlot)
          : dayCheckoutSlots(identity.hours, now, offset, undefined, identity.closures, tz, slotPrepMinutes, slotFirstSlot);
      // SC-75: a midi-only product in the cart must not be schedulable for an
      // evening slot (and vice-versa). Restrict the picker to the periods where
      // every cart product is available — matches sale_api's order-time guard.
      out.push({
        daySlots: openSlots,
        slots: filterSlotsByCartAvailability(openSlots, lines, menu?.items ?? []),
      });
    }
    return out;
  }, [identity?.hours, identity?.closures, slotPrepMinutes, slotFirstSlot, preorderMaxDays, lines, menu, now, tz]);

  // Le client arrive du tiroir avec « Plus de creneau aujourd'hui — mais vous
  // pouvez commander pour demain » : la carte « Horaire » doit s'ouvrir sur le
  // premier jour a venir qui a des creneaux (Demain d'abord, puis plus loin
  // dans l'horizon), sinon il lit « Aucun creneau disponible » et l'assistant
  // reste bloque tant qu'il n'a pas devine le bon onglet. Une seule fois,
  // avant tout choix manuel : ensuite l'onglet appartient au client.
  const dayAutoSwitchedRef = useRef(false);
  useEffect(() => {
    if (dayAutoSwitchedRef.current || isDineIn) return;
    if (dayData.length === 0 || (dayData[0]?.slots.length ?? 0) > 0) return;
    const target = dayData.findIndex((d, i) => i >= 1 && d.slots.length > 0);
    if (target === -1) return;
    dayAutoSwitchedRef.current = true;
    setSelectedDay(target);
    dispatch(setScheduledAt(''));
  }, [isDineIn, dayData, dispatch]);

  // Active slots depend on the selected day
  const activeDayData = dayData[selectedDay] ?? { daySlots: [], slots: [] };
  const activeDaySlots = activeDayData.daySlots;
  const activeSlots = activeDayData.slots;

  // SC-879 — l'heure que le client lit, où qu'il soit dans l'assistant : le sélecteur de
  // la carte « Horaire », la barre d'action et l'avis de report doivent tous
  // annoncer la même. `checkout.scheduledAt` peut être un rendu en retard sur la
  // liste (l'effet de recalage plus bas le rattrape), d'où le repli sur le
  // premier créneau, exactement comme le sélecteur le faisait seul.
  const selectedSlot = activeSlots.includes(checkout.scheduledAt) ? checkout.scheduledAt : activeSlots[0] ?? '';
  // SC-1500 — « Dès que possible » : offert aujourd'hui seulement, pour les modes
  // dont le restaurant l'a activé, et alors sélectionné par défaut. Gardé à part de
  // `checkout.scheduledAt` (qui reste un vrai créneau, le plus proche par défaut).
  const [asapPicked, setAsapPicked] = useState(true);
  const asapOffered = !isDineIn && selectedDay === 0 && activeSlots.length > 0 && asapEnabledFor(identity, checkout.mode);
  const asapActive = asapOffered && asapPicked;

  // SC-335 / SC-587: proactively flag every cart line the back-office has made
  // un-orderable — product or category deactivated, switched off for the chosen
  // fulfilment mode, restricted to a service that has no slot left today, or
  // carrying an option that was withdrawn. SC-335 covered only the mode case and
  // deliberately tolerated a product missing from the menu; that tolerance is
  // what let a deactivated dish ride all the way to an opaque 400 at payment.
  // Shares diagnoseCartLines with the cart drawer so the two screens agree.
  const blockedLines = useMemo<BlockedCartLine[]>(() => {
    if (!menu || lines.length === 0) return [];
    const remainingPeriods = !isDineIn && identity?.hours && slotPrepMinutes != null
      ? remainingPeriodsToday(identity.hours, now, slotPrepMinutes, identity.closures ?? [], tz, slotFirstSlot)
      : new Set<'lunch' | 'dinner'>();
    return diagnoseCartLines({
      lines,
      menu,
      mode: isDineIn ? 'dine_in' : checkout.mode,
      remainingPeriods,
    });
  }, [menu, isDineIn, lines, checkout.mode, identity, slotPrepMinutes, slotFirstSlot, now, tz, ct.lang]);
  const unavailableLineNames = useMemo(() => blockedLineNames(blockedLines), [blockedLines]);
  const hasUnavailableLines = blockedLines.length > 0;
  const dropBlockedLines = (entries: BlockedCartLine[]) => {
    for (const entry of entries) {
      dispatch(removeLine({ restaurantUuid, lineId: entry.lineId }));
    }
  };

  // Lines the SERVER refused at submit — the case no client check can catch,
  // because the customer's menu snapshot was already stale when the page loaded.
  // Same modal, same one-click fix as the drawer's pre-checkout re-check.
  const [rejectedLines, setRejectedLines] = useState<BlockedCartLine[]>([]);
  const blockedFromReject = (err: unknown): BlockedCartLine[] => {
    const ids = new Set(unavailableProductIds(err));
    if (ids.size === 0) return [];
    return lines
      .filter((line) => ids.has(line.itemId))
      .map((line) => ({
        lineId: line.lineId,
        itemId: line.itemId,
        name: catalogName(line.snapshot),
        reason: 'removed' as const,
      }));
  };

  // Switch the checkout to this restaurant. No-ops when the slice is
  // already bound to the same uuid; otherwise wipes mode/address/schedule/
  // payment/note (restaurant-specific) and keeps customer (personal).
  useEffect(() => {
    dispatch(bindRestaurant(restaurantUuid));
  }, [restaurantUuid, dispatch]);

  // SC-570: the fallback itself is legitimate — an order whose mode the backend
  // will reject must not be submittable. What broke trust was doing it in
  // silence: the customer picked Livraison in the cart and landed on a page that
  // simply read "A emporter". Remember that we took the choice away so the Mode
  // step can say why.
  const [deliveryDropped, setDeliveryDropped] = useState(false);
  useEffect(() => {
    // Only once both backends have answered — before that the list is the
    // provisional "known-orderable" one, and a stored `delivery` pick is absent
    // from it merely because we don't know yet.
    if (!modesResolved) return;
    if (allowedModes.length === 0 || allowedModes.includes(checkout.mode)) return;
    if (checkout.mode === 'delivery') setDeliveryDropped(true);
    dispatch(setMode(allowedModes[0]));
  }, [modesResolved, allowedModes, checkout.mode, dispatch]);

  // SC-568: which concrete time the clock pushed out from under the customer,
  // so the Horaire step can say "12:20 n'est plus disponible → 12:30" instead
  // of moving the pickup time silently. Cleared on an explicit re-pick.
  const [slotAdvancedFrom, setSlotAdvancedFrom] = useState<string | null>(null);
  // Le client a-t-il lui-même choisi l'heure retenue ? Changer de mode change le
  // temps de préparation, donc le premier créneau : « 12:20 n'est plus
  // disponible » ne se dit alors que si 12:20 venait du client. Le premier
  // créneau que la page avait pris d'office pour la vente à emporter n'a rien
  // à voir avec la livraison.
  const slotPickedRef = useRef(false);
  const slotModeRef = useRef(checkout.mode);

  // No "Au plus vite" option anymore — when the stored scheduledAt isn't
  // a real available slot (initial 'asap', stale, etc.) snap to the first
  // available one so the order payload always carries a concrete time.
  useEffect(() => {
    const modeSwitched = slotModeRef.current !== checkout.mode;
    slotModeRef.current = checkout.mode;
    if (activeSlots.length === 0) return;
    if (activeSlots.includes(checkout.scheduledAt)) return;
    // Announce only when a real time got overtaken by the clock (or by a longer
    // prep after a mode switch, if the customer had picked that time); the
    // initial 'asap' placeholder — and a stored slot that a cart/period change
    // moved backwards — snap silently as before.
    if (
      checkout.scheduledAt !== 'asap' &&
      checkout.scheduledAt < activeSlots[0] &&
      (!modeSwitched || slotPickedRef.current)
    ) {
      setSlotAdvancedFrom(checkout.scheduledAt);
    }
    slotPickedRef.current = false;
    dispatch(setScheduledAt(activeSlots[0]));
  }, [activeSlots, checkout.scheduledAt, checkout.mode, dispatch]);

  useEffect(() => {
    if (enabledChannels.length === 0) return;
    if (!enabledChannels.includes(checkout.payment.channel)) {
      dispatch(setChannel(enabledChannels[0]));
    }
  }, [enabledChannels, checkout.payment.channel, dispatch]);

  useEffect(() => {
    if (!isAuthed || !currentUser) return;
    const patch: Partial<typeof checkout.customer> = {};
    if (!checkout.customer.name && currentUser.name) patch.name = currentUser.name;
    if (!checkout.customer.phone && currentUser.phone) patch.phone = currentUser.phone;
    if (!checkout.customer.email && currentUser.email) patch.email = currentUser.email;
    if (Object.keys(patch).length > 0) {
      dispatch(setCustomer(patch));
    }
    if (defaultAddr && !checkout.address.street) {
      dispatch(setAddress({
        street: defaultAddr.street,
        city: defaultAddr.city,
        zip: defaultAddr.zip,
        instructions: defaultAddr.instructions ?? '',
        // Geocoded fields persisted from a previous checkout: when present,
        // the auto-quote effect below fires immediately and the user never
        // needs to open Google Places Autocomplete again.
        place_id: defaultAddr.place_id,
        lat: defaultAddr.lat,
        lng: defaultAddr.lng,
        formatted_address: defaultAddr.formatted_address,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, restaurantUuid, currentUser]);

  // Persist a newly picked geocoded address to the user's address book so
  // future checkouts can skip Google Places Autocomplete entirely. Keyed on
  // place_id; if the user already has this address saved we don't duplicate.
  useEffect(() => {
    if (!isAuthed) return;
    const a = checkout.address;
    if (!a.place_id || a.lat == null || a.lng == null) return;
    if (savedAddresses.some((s) => s.place_id === a.place_id)) return;
    dispatch(addAddress({
      id:
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `addr-${Date.now()}`,
      label: a.formatted_address ?? `${a.street}, ${a.zip} ${a.city}`.trim(),
      street: a.street,
      city: a.city,
      zip: a.zip,
      instructions: a.instructions,
      isDefault: savedAddresses.length === 0,
      place_id: a.place_id,
      lat: a.lat,
      lng: a.lng,
      formatted_address: a.formatted_address,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, checkout.address.place_id]);

  // Server-quoted delivery fee (in cents). Replaces the previous hardcoded
  // "subtotal < 30€ → 3€" rule. When the quote isn't successful, fee is 0
  // and the Continue button is gated below.
  const deliveryFee =
    checkout.mode === 'delivery' && quoteStatus.status === 'ok'
      ? Math.round(parseFloat(quoteStatus.fee_ttc) * 100)
      : 0;

  // Fix 14: countdown state for ROUND_COOLDOWN (seconds)
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const id = window.setInterval(() => {
      setCooldownRemaining((prev) => {
        if (prev <= 1) { window.clearInterval(id); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [cooldownRemaining]);

  // SC-316: when the backend rejects the chosen pickup/delivery slot as expired,
  // block order validation and keep a persistent message until the customer
  // picks a new slot. Otherwise the security-check re-run lets a second click
  // raise a generic "une erreur est survenue" toast that buries the real
  // "slot expired, pick another" message, and the customer keeps retrying blindly.
  const [slotExpired, setSlotExpired] = useState(false);
  useEffect(() => {
    // Clearing on any scheduledAt change covers both an explicit re-pick and an
    // auto-reselect once a fresh valid slot replaces the expired one.
    setSlotExpired(false);
  }, [checkout.scheduledAt]);

  // Promotion selection mirrors resource_api's server-authoritative pick.
  // selectPromotion and nextPromotionHint operate in cents (integers).
  const orderMode: 'emporter' | 'livraison' =
    checkout.mode === 'delivery' ? 'livraison' : 'emporter';
  const promotions = config?.promotions ?? [];
  // DF-0212 — le seuil regarde le panier entier, le pourcentage la seule part
  // remisable (produit ET catégorie cochés). Même calcul que le devis serveur :
  // diverger d'ici renvoie la commande en `total_mismatch`.
  const eligibleSubtotal = eligibleDiscountSubtotal(lines, menu);
  // SC-1081 — validite des promotions au jour civil du restaurant.
  const promoToday = todayIsoInTz(tz);
  const promoMatch = selectPromotion(promotions, subtotal, orderMode, eligibleSubtotal, promoToday);
  const promoHint = promoMatch ? null : nextPromotionHint(promotions, subtotal, orderMode, promoToday);
  // Fix 2: for dine-in force discount=0 and deliveryFee=0 (server excludes promos for sur_place)
  const discount = isDineIn ? 0 : (promoMatch?.discountAmountCents ?? 0);
  const effectiveDeliveryFee = isDineIn ? 0 : deliveryFee;
  // SC-739 — les questions posées avant validation. Leur supplément s'ajoute au
  // total encaissé mais reste HORS de `subtotal` : c'est lui que la promotion et
  // le minimum de commande regardent, côté client comme côté serveur, et un
  // supplément de sauce ne doit peser sur ni l'un ni l'autre.
  // SC-748 — le mode de vente décide QUELLES questions existent, le montant des
  // plats décide lesquelles sont posées maintenant. `subtotal` est la même base
  // que le devis serveur : hors promotion, hors frais de livraison, hors
  // supplément d'option.
  // SC-753 — le mode de vente d'une commande à table est `dine_in`, jamais
  // `checkout.mode` : celui-ci ne connaît que les modes en ligne (emporter /
  // livraison) et retombe sur le premier autorisé pendant tout le parcours à
  // table. Le lire ici posait au client assis les questions configurées pour
  // « À emporter », et taisait celles configurées pour « Sur place ».
  const cartOptionGroupsForMode = useMemo(
    () => groupsForMode(menu?.cartOptionGroups, isDineIn ? 'dine_in' : checkout.mode),
    [menu?.cartOptionGroups, isDineIn, checkout.mode],
  );
  const cartOptionGroups = useMemo(
    () => groupsForAmount(cartOptionGroupsForMode, subtotal),
    [cartOptionGroupsForMode, subtotal],
  );
  // Ce qu'il manque pour débloquer la question la plus proche. Posé sur les
  // questions du mode, AVANT le filtre de montant : celles que le montant
  // écarte sont justement celles à annoncer.
  const cartOptionHint = useMemo(
    () => nextCartOptionHint(cartOptionGroupsForMode, subtotal),
    [cartOptionGroupsForMode, subtotal],
  );
  // Une réponse à une question qui n'est plus posée (mode changé, panier repassé
  // sous un seuil) ne part pas au serveur : il la refuserait avec un 400 que le
  // client ne pourrait pas relier à quoi que ce soit de visible.
  const answeredCartOptions = useMemo(
    () => selectionsForGroups(cartOptionGroups, checkout.cartOptions),
    [cartOptionGroups, checkout.cartOptions],
  );
  const cartOptionsTotal = useMemo(
    () => cartOptionsTotalCents(cartOptionGroups, checkout.cartOptions),
    [cartOptionGroups, checkout.cartOptions],
  );
  const missingCartOptions = useMemo(
    () => unansweredRequiredGroups(cartOptionGroups, checkout.cartOptions),
    [cartOptionGroups, checkout.cartOptions],
  );
  const total = Math.max(0, subtotal - discount + effectiveDeliveryFee + cartOptionsTotal);
  // SC-1091 — the loyalty discount, EXACT and in integer cents, mirroring sale_api
  // (`views_online.py`): it bites into the goods AFTER the order promotion only;
  // delivery and cart options are never discounted. `finalTotal` is what the
  // customer sees AND what is sent as `total_ttc` — the server compares it to the
  // cent and answers `total_mismatch` if the screen was wrong, so nobody is ever
  // charged an amount different from the one shown.
  // `appliedAdvantage` = the code selected is one whose type and value we know (a
  // wallet coupon, or a reward just exchanged). A code typed by hand and absent
  // from the wallet has no known value: the server prices it, `total_ttc` is
  // omitted and the screen says « remise calculée au paiement ».
  //
  // A table (dine-in) order NEVER carries an advantage: sale_api's dine-in branch
  // ignores `redemption_code` and prices without it, so offering one there would
  // show a discount that is never applied — or send a `total_ttc` the server
  // refuses. Same gate for the step, the discount, the total and the payload.
  //
  // SC-1628 — programme switched off: no exchange and no points any more, but a
  // coupon the customer already holds is still taken by the order, so the step
  // stays as long as the wallet has one usable online.
  const programOn = Boolean(loyalty?.enabled);
  const holdsOnlineCoupon = (loyaltyCard?.wallet ?? []).some((c) =>
    isUsableOnline({ type: c.reward_type, value: c.value, usable_online: c.usable_online }),
  );
  const redemptionActive = Boolean(
    isAuthed && (programOn || holdsOnlineCoupon) && !isDineIn && !programDisabled,
  );
  const goodsAfterPromo = Math.max(0, subtotal - discount);
  const appliedAdvantage =
    redemptionActive && redemptionCode && loyaltySelection?.code === redemptionCode
      ? loyaltySelection
      : null;
  const { redemptionCents, finalTotalCents: finalTotal } = appliedAdvantage
    ? totalAfterRedemption(total, goodsAfterPromo, appliedAdvantage)
    : { redemptionCents: 0, finalTotalCents: total };
  const codeValueUnknown = Boolean(redemptionActive && redemptionCode && !appliedAdvantage);
  // What logging in would earn on THIS order — same rule as for a logged-in customer
  // (`earnEstimate` on the final total), a number only for online payment.
  const guestNudgeInfo =
    isAuthed || isDineIn ? null : guestNudge(guestProgram, total, checkout.payment.channel);
  const guestBannerVisible = guestNudgeInfo !== null && !guestBannerDismissed;
  // Stripe refuses a card payment under 0,50 € and sale_api refuses the order first
  // (`online_amount_too_small`): when the advantage leaves less than that to pay
  // ONLINE, don't offer a zero-amount card form nor a submit that would bounce —
  // say so, and offer on-site payment when the restaurant has it.
  const onlineTooSmall = checkout.payment.channel === 'online' && leavesTooLittleOnline(finalTotal, redemptionCents);
  const tooSmallText = paymentOnsiteEnabled
    ? t('loyalty.checkout.tooSmall')
    : t('loyalty.checkout.tooSmallNoOnsite');
  const payOnsiteInstead = () => {
    dispatch(setChannel('on_site'));
  };
  // « Cette commande vous rapportera environ N pts » — the hub credits
  // `int(total_ttc × per_euro) + per_visit` on the FINAL total, and only for an
  // order paid ONLINE (payment webhook). Pay-on-site earns nothing here: the
  // customer is told to show the card at the counter instead.
  const loyaltyOn = redemptionActive && programOn;
  const earnPoints =
    loyaltyOn && checkout.payment.channel === 'online'
      ? earnEstimate(finalTotal, loyaltyCard?.points_per_euro, loyaltyCard?.points_per_visit)
      : 0;
  const earnAtCounter = loyaltyOn && checkout.payment.channel === 'on_site';

  // Minimum de commande : total ARTICLES avant la promotion de commande et avant
  // les frais de livraison — la même base que le serveur (409
  // order_below_minimum). SC-749 : le seuil vient du même champ que le panier
  // (`min_order_amount_*`), plus de la config de livraison.
  // SC-749 (suite) : le seuil suit le MODE, il n'est plus réservé à la
  // livraison. Le sélecteur de mode est sur cette page : un panier constitué en
  // « à emporter » puis basculé en « Livraison » ici — ou l'inverse — ne
  // repassait jamais devant le garde du tiroir, et le client ne découvrait le
  // refus qu'au 409, après avoir tout rempli.
  // SC-952 : la promotion ne se déduit plus de cette base. Elle l'était ici et
  // nulle part ailleurs — le tiroir du panier compare depuis toujours `subtotal`
  // seul — donc un panier de 22 € passait le tiroir avec un minimum à 20 €, puis
  // l'étape « Mode » virait au rouge sur cette page dès que 10 % de promotion le
  // ramenaient à 19,80 €, et la commande devenait invalidable. Le serveur suit la
  // même base depuis le même ticket.
  const minOrderCents = minOrderCentsForOrder(
    config,
    isDineIn ? 'dine_in' : orderMode,
    deliveryConfig,
    quoteStatus.status === 'ok' ? quoteStatus : null,
  );
  const belowOrderMin = minOrderCents > 0 && subtotal < minOrderCents;
  const orderMinMissingCents = Math.max(0, minOrderCents - subtotal);

  const hasAccountEmail = Boolean(isAuthed && currentUser?.email);

  // Structural blockers only: the CTA stays clickable when the only issue is
  // a missing user-fillable field, so onSubmit can scroll the user to it
  // instead of leaving them in front of a silently-dead button.
  const submitDisabled = useMemo(() => {
    if (isLoading) return true;
    if (isMenuFetching) return true;
    if (lines.length === 0) return true;
    // SC-739 — une question obligatoire sans réponse : le devis la refuserait
    // avec un 400 opaque. Placé avant la sortie anticipée « sur place », qui
    // saute les vérifications suivantes.
    if (missingCartOptions.length > 0) return true;
    // Spec 2026-08-28 paid-table-ordering-lock §4 — la table a ete reglee au QR.
    // AVANT la sortie anticipee « sur place » ci-dessous, qui rendrait la garde
    // morte precisement dans le seul cas ou elle sert.
    if (orderingLocked) return true;
    // SC-1091: the advantage leaves under the card minimum online — nothing to send.
    if (onlineTooSmall) return true;
    // Dine-in: payment is always on_site, skip payments/modes/slots structural checks
    if (isDineIn) return false;
    if (paymentsBlocked) return true;
    if (quoteStatus.status === 'city_not_served') return true;
    if (allowedModes.length === 0) return true;
    if (activeSlots.length === 0) return true;
    return false;
  }, [
    isLoading,
    isMenuFetching,
    lines.length,
    missingCartOptions.length,
    orderingLocked,
    onlineTooSmall,
    isDineIn,
    paymentsBlocked,
    quoteStatus.status,
    allowedModes.length,
    activeSlots.length,
  ]);

  // Dine-in: name optional (falls back to "Table N" server-side), phone/email not required
  const refused = (field: ContactField) =>
    refusedContact?.field === field && refusedContact.value === checkout.customer[field];
  const nameInvalid = isDineIn ? false : (
    !checkout.customer.name.trim() ||
    checkout.customer.name.trim().length > CUSTOMER_NAME_MAX ||
    refused('name')
  );
  const phoneDigits = checkout.customer.phone.replace(/\D/g, '');
  const phoneInvalid = isDineIn ? false : (
    !checkout.customer.phone.trim() ||
    phoneDigits.length < 8 ||
    checkout.customer.phone.trim().length > CUSTOMER_PHONE_MAX ||
    refused('phone')
  );
  // DF-0573 — the server's own rule (Django EmailField), not « has @ and . ».
  const emailInvalid = isDineIn ? false : (!hasAccountEmail && (
    !isValidEmail(checkout.customer.email) || refused('email')
  ));
  const addressFieldsComplete =
    Boolean(checkout.address.street.trim()) &&
    Boolean(checkout.address.zip.trim()) &&
    Boolean(checkout.address.city.trim()) &&
    destinationKey !== null;
  const addressIncomplete =
    checkout.mode === 'delivery' && (!addressFieldsComplete || quoteStatus.status !== 'ok');

  // SC-760 — l'état de chaque étape, source unique du fil du haut, du cadre de
  // la section et du rappel « à compléter » de la barre d'action. Une étape
  // passe au rouge dès qu'elle EMPÊCHE l'envoi ; un champ que le client peut
  // encore remplir (nom, téléphone, adresse) n'alarme qu'après une tentative,
  // sinon la page crie sur un formulaire simplement vide.
  const contactIncomplete = nameInvalid || phoneInvalid || emailInvalid;
  const customerAttempted = attempted('step-customer');
  const steps = useMemo<CheckoutStep[]>(() => {
    const fillable = (incomplete: boolean, id: string): CheckoutStep['state'] =>
      incomplete ? (attempted(id) ? 'blocked' : 'todo') : 'done';
    const list: CheckoutStep[] = [];
    if (isDineIn) {
      list.push({ id: 'step-recap', label: t('checkout.dineIn.recapHeading'), state: 'done' });
    } else {
      // Une étape sans choix est une étape de trop : quand le restaurant n'offre
      // qu'un mode, il est déjà retenu (l'effet plus haut le pose sur le panier)
      // et la carte n'afficherait qu'un bouton déjà vert. On ne la retire
      // toutefois qu'une fois les deux backends d'accord : avant résolution la
      // liste vaut « à emporter » seul (SC-570, elle échoue fermée), et la
      // retirer là ferait réapparaître l'étape quand la livraison arrive.
      if (!modesResolved || allowedModes.length !== 1) {
        list.push({
          id: 'step-mode',
          label: t('checkout.steps.mode'),
          state: modesResolved && allowedModes.length === 0 ? 'blocked' : 'done',
        });
      }
      if (checkout.mode === 'delivery') {
        list.push({
          id: 'step-address',
          label: t('checkout.steps.address'),
          state: fillable(addressIncomplete, 'step-address'),
        });
      }
      list.push({
        id: 'step-customer',
        label: t('checkout.steps.customer'),
        state: fillable(contactIncomplete, 'step-customer'),
      });
      list.push({
        id: 'step-time',
        label: t('checkout.steps.time'),
        state: activeSlots.length === 0 || slotExpired ? 'blocked' : 'done',
      });
    }
    if (cartOptionGroups.length > 0) {
      // Une question obligatoire est un champ à remplir comme un autre : elle ne
      // rougit qu'après une tentative. Rouge dès l'arrivée, elle criait « À
      // compléter » sur la première étape, avant même que le client l'ait vue.
      list.push({
        id: 'step-options',
        label: t('checkout.cartOptions.title'),
        state: fillable(missingCartOptions.length > 0, 'step-options'),
      });
    }
    list.push({ id: 'step-note', label: t('checkout.steps.note'), state: 'done', optional: true });
    if (redemptionActive) {
      list.push({
        id: 'step-loyalty',
        label: t('loyalty.checkout.label'),
        // B1: a code the server refused blocks the send until it is fixed.
        state: loyaltyRejected ? 'blocked' : 'done',
        optional: true,
      });
    }
    if (!isDineIn) {
      list.push({
        id: 'step-payment',
        label: t('checkout.steps.payment'),
        state: paymentsEmpty || onlineTooSmall ? 'blocked' : 'done',
      });
    }
    return list;
  }, [
    t,
    isDineIn,
    submitAttempted,
    attemptedSteps,
    modesResolved,
    allowedModes.length,
    checkout.mode,
    belowOrderMin,
    addressIncomplete,
    contactIncomplete,
    activeSlots.length,
    slotExpired,
    cartOptionGroups.length,
    missingCartOptions.length,
    redemptionActive,
    loyaltyRejected,
    paymentsEmpty,
    onlineTooSmall,
  ]);

  // SC-775 — l'assistant : une seule étape à l'écran. `activeStepId` n'est
  // qu'une préférence, pas une vérité — la liste des étapes bouge sous le client
  // (passage en livraison, question qui apparaît au-delà d'un montant, « Mode »
  // qui s'efface quand les deux backends s'accordent sur un mode unique), donc
  // on retombe sur la première étape dès que l'étape retenue n'existe plus,
  // plutôt que d'afficher une page vide. Toute étape conditionnelle doit donc
  // pouvoir tomber sans perdre le client : celles d'aujourd'hui s'effacent
  // toutes AVANT l'étape affichée, et la première étape restante est bien la
  // suite du parcours.
  const [activeStepId, setActiveStepId] = useState<string | null>(null);
  const currentStepId =
    activeStepId && steps.some((s) => s.id === activeStepId) ? activeStepId : steps[0]?.id ?? '';
  const currentIndex = steps.findIndex((s) => s.id === currentStepId);
  const isLastStep = steps.length > 0 && currentIndex === steps.length - 1;
  // L'écran qui porte l'invitation au compte d'un invité, en haut de page.
  const guestPromptStep =
    !isAuthed && !isDineIn && currentIndex === 0 && currentStepId !== 'step-customer';

  const pageTopRef = useRef<HTMLDivElement>(null);

  // Au plus une fois par numéro : le profil n'est écrit qu'en quittant l'étape
  // « Coordonnées » avec un numéro valide, jamais à chaque frappe. Un échec ne
  // bloque rien — la commande porte le numéro de toute façon — et laisse
  // réessayer au passage suivant.
  const savedPhoneRef = useRef<string | null>(null);
  const savePhoneToProfile = () => {
    if (!isAuthed || !phoneMissing || isDineIn || phoneInvalid) return;
    const phone = checkout.customer.phone.trim();
    if (savedPhoneRef.current === phone) return;
    savedPhoneRef.current = phone;
    void dispatch(updateMeUser({ phone })).then((result) => {
      if (updateMeUser.rejected.match(result)) savedPhoneRef.current = null;
    });
  };

  const goToStep = (id: string) => {
    if (currentStepId === 'step-customer' && id !== 'step-customer') savePhoneToProfile();
    setActiveStepId(id);
    // Le haut de la page porte le fil des étapes : c'est là que le client doit
    // atterrir, pas au milieu de l'étape qu'il vient de quitter. On remonte via
    // le conteneur et non `window.scrollTo` — `Element.scrollIntoView` est la
    // seule des deux API que l'environnement de test fournit.
    pageTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // SC-1013 — ce que le client a déjà tapé à l'étape « Coordonnées » part avec
  // la fenêtre d'inscription : il n'a plus que son mot de passe à choisir (ou
  // le bouton Google). Le nom y est un champ unique, l'inscription en demande
  // deux — le premier mot est le prénom, le reste le nom.
  const openRegisterFromCheckout = () => {
    const [firstName = '', ...rest] = checkout.customer.name.trim().split(/\s+/);
    dispatch(
      openAccountModal({
        mode: 'register',
        redirectTo: `${location.pathname}${location.search}`,
        prefill: {
          firstName,
          lastName: rest.join(' '),
          email: checkout.customer.email.trim(),
          phone: checkout.customer.phone.trim(),
        },
      }),
    );
  };

  // SC-1091 — same way back as the register flow: the account modal returns to this
  // very page when it is done; the cart and the typed checkout fields live in the
  // store, so nothing is lost (the post-login effect only fills EMPTY fields).
  const openLoginFromCheckout = () => {
    dispatch(openAccountModal({ mode: 'login', redirectTo: `${location.pathname}${location.search}` }));
  };

  const stepProps = (id: string) => {
    const index = steps.findIndex((s) => s.id === id);
    return {
      step: steps[index] ?? { id, label: '', state: 'done' as const },
      index: index + 1,
      hidden: id !== currentStepId,
    };
  };

  // Ce qui empêche de passer à l'étape suivante. Même lecture que l'état du fil
  // des étapes : « Suivant » ne peut pas être plus permissif que la pastille.
  const stepIncomplete = (id: string): boolean => {
    if (id === 'step-mode') return modesResolved && allowedModes.length === 0;
    if (id === 'step-address') return addressIncomplete;
    if (id === 'step-customer') return contactIncomplete;
    if (id === 'step-time') return activeSlots.length === 0 || slotExpired;
    if (id === 'step-options') return missingCartOptions.length > 0;
    return false;
  };

  // Ce qui empêche l'envoi, dans l'ordre de la page. La ligne de panier devenue
  // incommandable passe devant : elle bloque le bouton elle aussi, et son
  // bandeau est tout en haut — donc hors de vue depuis la barre d'action.
  const firstBlocker = useMemo<CheckoutStep | null>(() => {
    if (hasUnavailableLines) {
      return { id: 'checkout-cart-alert', label: t('checkout.steps.cart'), state: 'blocked' };
    }
    // Le minimum de commande ne se répare pas dans l'assistant, il se répare au
    // menu. Il ne rougit donc plus l'étape « Mode » — qui n'est pour rien dans
    // le total, et qui disparaît quand le restaurant n'offre qu'un mode — mais
    // son propre bandeau de page, cible de ce raccourci.
    if (!isDineIn && belowOrderMin) {
      return { id: 'checkout-order-min', label: t('checkout.steps.cart'), state: 'blocked' };
    }
    const blocked = steps.find((s) => s.state === 'blocked');
    if (blocked) return blocked;
    // Une question sans réponse grise l'envoi (`submitDisabled`) : sur la
    // dernière étape, le bouton gris doit dire pourquoi, tentative ou pas.
    if (isLastStep && missingCartOptions.length > 0) {
      return steps.find((s) => s.id === 'step-options') ?? null;
    }
    return null;
  }, [hasUnavailableLines, isDineIn, belowOrderMin, steps, t, isLastStep, missingCartOptions.length]);

  function scrollSectionIntoView(el: HTMLElement | null) {
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function focusInput(input: HTMLInputElement | null) {
    if (!input) return;
    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => input.focus({ preventScroll: true }), 350);
  }

  // SC-775 — l'étape fautive n'est plus forcément celle qui est à l'écran : on y
  // va d'abord. Le focus suit une fois la section rendue (`focusInput` attend
  // déjà la fin du défilement pour poser le curseur).
  // `only` : « Suivant » ne cherche que dans l'étape affichée — c'est elle qui
  // vient de refuser d'avancer. Sans `only` (envoi), la première en ordre de page.
  function focusFirstInvalid(only?: string): boolean {
    const inStep = (id: string) => !only || only === id;
    if (addressIncomplete && inStep('step-address')) {
      goToStep('step-address');
      setTimeout(() => scrollSectionIntoView(addressSectionRef.current), 0);
      return true;
    }
    if (inStep('step-customer')) {
      if (nameInvalid) { goToStep('step-customer'); focusInput(nameRef.current); return true; }
      if (phoneInvalid) { goToStep('step-customer'); focusInput(phoneRef.current); return true; }
      if (emailInvalid) { goToStep('step-customer'); focusInput(emailRef.current); return true; }
    }
    // « Suivant » sur une question obligatoire sans réponse ne faisait rien de
    // visible : la seconde question était 600 px plus bas, hors de l'écran.
    if (missingCartOptions.length > 0 && inStep('step-options')) {
      goToStep('step-options');
      const groupId = missingCartOptions[0].id;
      setTimeout(() => {
        const group = document.querySelector<HTMLElement>(`[data-testid="cart-option-group-${groupId}"]`);
        group?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        group?.querySelector<HTMLButtonElement>('button:not([disabled])')?.focus({ preventScroll: true });
      }, 0);
      return true;
    }
    return false;
  }

  // « Suivant » n'enjambe jamais un champ obligatoire : l'étape passe au rouge
  // (c'est `attemptedSteps` qui l'y met) et le champ fautif prend le focus,
  // au lieu de laisser le client buter trois étapes plus loin sur un bouton gris.
  const onNextStep = () => {
    if (stepIncomplete(currentStepId)) {
      setAttemptedSteps((prev) => (prev.includes(currentStepId) ? prev : [...prev, currentStepId]));
      focusFirstInvalid(currentStepId);
      return;
    }
    const next = steps[currentIndex + 1];
    if (next) goToStep(next.id);
  };

  // SC-346: `round` pins the just-sent dine-in round so the confirmation recap shows
  // only those dishes (rounds merge into one anchor order). Null for online orders.
  const confirmationUrl = (orderId: string, orderAccessToken?: string | null, round?: string | null) => {
    const path = isDineIn && tableSession?.tableToken
      ? `/${restaurantUuid}/table/${tableSession.tableToken}/confirmation/${orderId}`
      : `/${restaurantUuid}/confirmation/${orderId}`;
    const params = new URLSearchParams();
    if (orderAccessToken) params.set('access_token', orderAccessToken);
    if (round != null) params.set('round', String(round));
    const qs = params.toString();
    return qs ? `${path}?${qs}` : path;
  };

  const finalizeOrder = (orderId: string, orderAccessToken?: string | null, round?: string | null) => {
    if (!isDineIn && checkout.mode === 'delivery' && checkout.address.place_id) {
      const matched = savedAddresses.find((a) => a.place_id === checkout.address.place_id);
      if (matched && !matched.isDefault) {
        dispatch(setDefaultAddress({ id: matched.id }));
      }
    }
    // Clear advisory buffet budget gate on successful order
    dispatch(setBuffetBudget({ restaurantUuid, maxItems: 0 }));
    dispatch(setCustomerNote(''));
    dispatch(clearCartOptions());
    dispatch(setRedemptionCodeAction(''));
    dispatch(clearRestaurantCart({ restaurantUuid }));
    navigate(confirmationUrl(orderId, orderAccessToken, round));
  };

  // SC-1155 : la cause réelle du refus d'une adresse complète, pour la ligne
  // rouge de l'étape et pour validateBeforePlace — un seul endroit, pas de dérive.
  const deliveryQuoteError = (): string | null => {
    if (quoteStatus.status === 'ok') return null;
    if (quoteStatus.status === 'city_not_served') return t('checkout.errors.deliveryCityNotServed');
    if (quoteStatus.status === 'out_of_range') return t('checkout.errors.deliveryAddressOutOfRange');
    return t('checkout.errors.deliveryFeeUnconfirmed');
  };

  const validateBeforePlace = (): string | null => {
    if (lines.length === 0) return t('cart.empty');
    // Dine-in: skip payment/address/slot validations
    if (!isDineIn) {
      if (paymentsEmpty) return t('checkout.errors.noPaymentConfigured');
    }
    if (onlineTooSmall) return tooSmallText;
    if (nameInvalid) return t('reservation.invalidName');
    if (phoneInvalid) return t('reservation.invalidPhone');
    if (emailInvalid) return t('reservation.invalidEmail');
    if (!isDineIn && checkout.mode === 'delivery') {
      if (
        !checkout.address.street.trim() ||
        !checkout.address.zip.trim() ||
        !checkout.address.city.trim()
      ) {
        return t('checkout.errors.addressIncomplete');
      }
      if (!destinationKey) {
        return t('checkout.errors.addressNotChosen');
      }
      const quoteError = deliveryQuoteError();
      if (quoteError) return quoteError;
    }
    // Hors du bloc « livraison » : le seuil « à emporter » existe aussi, et
    // c'est lui qui n'était contrôlé nulle part sur cette page.
    if (belowOrderMin) {
      return t('checkout.errors.orderBelowMinimum', {
        minimum: (minOrderCents / 100).toFixed(2).replace('.', ','),
      });
    }
    // SC-574: without a token the POST comes back 400 captcha_failed, which read
    // as "order failed, try again" — so the customer retried into the same wall.
    // The managed widget usually resolves in a second; say "wait" instead of
    // spending an order attempt on it. Same gate the shared-table send uses.
    // SC-760: checked LAST — the widget is only mounted once the fields are
    // filled, so "patientez" must never be the answer to a missing address.
    if (needCaptcha && !recaptchaToken && !captchaBroken) {
      return t('checkout.errors.recaptchaRequired');
    }
    return null;
  };

  // SC-568: the 30 s refresh can still be seconds stale when the customer finally
  // hits "Payer". Re-derive the list from a fresh clock at that exact moment and
  // move to the new earliest slot if the picked one just expired — sale_api would
  // otherwise reject the whole order with scheduled_at_in_past. Returns the ISO
  // datetime to send, or null when nothing is bookable today anymore.
  const resolveScheduledAtNow = (): string | null => {
    if (!identity?.hours || slotPrepMinutes == null) return null;
    const at = new Date();
    if (selectedDay >= 1) {
      // J+N (N = selectedDay): no live-clock expiry concern, just re-check closures/hours
      const fresh = filterSlotsByCartAvailability(
        dayCheckoutSlots(identity.hours, at, selectedDay, undefined, identity.closures, tz, slotPrepMinutes, slotFirstSlot),
        lines,
        menu?.items ?? [],
      );
      if (fresh.length === 0) return null;
      const slot = fresh.includes(checkout.scheduledAt) ? checkout.scheduledAt : fresh[0];
      if (slot !== checkout.scheduledAt) {
        dispatch(setScheduledAt(slot));
      }
      // DF-0330 — the target day is the restaurant's calendar day + N, not the
      // browser's (Paris browser, Cayenne restaurant can disagree on "tomorrow").
      // SC-1090 — and not `at + N×24h` either: across a DST change inside the
      // horizon a 23h/25h day shifts that instant onto the wrong date. Anchor
      // on the slot's own instant so the UTC offset is the slot's, too.
      const p = localParts(at, tz);
      const day = new Date(Date.UTC(p.year, p.month - 1, p.day + selectedDay));
      const [sh, sm] = slot.split(':').map(Number);
      const targetDate = dateFromLocal(day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate(), sh, sm, tz);
      return composeScheduledIso(slot, at, identity.country, targetDate, identity.timezone);
    }
    const fresh = filterSlotsByCartAvailability(
      todayCheckoutSlots(identity.hours, at, slotPrepMinutes, undefined, identity.closures, tz, slotFirstSlot),
      lines,
      menu?.items ?? [],
    );
    if (fresh.length === 0) return null;
    // SC-1500 — « Dès que possible » : on envoie le créneau le plus proche, sans
    // toucher à la sélection ni afficher l'avis de report.
    if (asapActive) {
      return composeScheduledIso(fresh[0], at, identity.country, undefined, identity.timezone);
    }
    const slot = fresh.includes(checkout.scheduledAt) ? checkout.scheduledAt : fresh[0];
    if (slot !== checkout.scheduledAt) {
      if (checkout.scheduledAt !== 'asap' && checkout.scheduledAt < slot) {
        setSlotAdvancedFrom(checkout.scheduledAt);
      }
      dispatch(setScheduledAt(slot));
      setNow(at);
    }
    return composeScheduledIso(slot, at, identity.country, undefined, identity.timezone);
  };

  // `total_mismatch`: the server priced the order differently from the screen (a
  // price moved, a promotion or the advantage changed). Re-read what the total is
  // built from — the menu (the reprice effect then tells which line moved) and the
  // customer's wallet — and say so; the server stays the authority, nobody is
  // charged the wrong amount.
  const refreshAfterMismatch = () => {
    try {
      void refetchMenu();
    } catch {
      /* the menu query never started (skipped): nothing to re-read */
    }
    dispatch(loyaltyApi.util.invalidateTags(['LoyaltyCard', 'LoyaltyRewards', 'LoyaltyBalance']));
  };
  // DF-0573 — once the customer changes anything the order is built from, the
  // last refusal no longer describes what the next send would get.
  useEffect(() => {
    setLastSendFailure(null);
  }, [checkout.customer, checkout.address, checkout.mode, checkout.payment, checkout.scheduledAt, subtotal, lines.length]);

  // DF-0573 — a click refused before anything is sent. While the token renews
  // after a refusal, and nothing has changed, the honest answer is the
  // server's last one, not the anti-robot's.
  const refuseBeforeSend = (validationError: string) => {
    if (validationError === t('checkout.errors.recaptchaRequired') && lastSendFailure) {
      dispatch(showErrorToast(lastSendFailure));
      return;
    }
    noteCaptchaRefusal(validationError);
    dispatch(showErrorToast(validationError));
    focusFirstInvalid();
  };

  // DF-0573 — every refused send goes to the hub's diagnostic log, stitched to
  // the captcha events of the same visit. Field paths and server enums only.
  const reportSendFailure = (err: unknown) => {
    const data = (err as { data?: { code?: unknown; detail?: unknown } } | undefined)?.data;
    reportSubmitErrorDiagnostic({
      restaurantUuid,
      session: captchaSessionRef.current,
      status: (err as { status?: number | string } | undefined)?.status,
      fields: rejectedFieldKeys(err),
      code: typeof data?.code === 'string' ? data.code : undefined,
      detail: typeof data?.detail === 'string' ? data.detail.slice(0, 100) : undefined,
    });
  };

  const orderFailureMessage = (err: unknown): string => {
    // DF-0573 — a refused contact field: back to « Vos coordonnées », the field
    // marked and focused, instead of a toast that names nothing.
    const contact = rejectedContactField(err);
    if (contact && !isDineIn) {
      setRefusedContact({ field: contact, value: checkout.customer[contact] });
      goToStep('step-customer');
      focusInput({ name: nameRef, phone: phoneRef, email: emailRef }[contact].current);
    }
    const data = (err as { data?: { delivery?: unknown; city?: unknown; sector?: unknown } })?.data;
    if (Array.isArray(data?.delivery) && data.delivery.includes('CITY_NOT_SERVED') &&
        destinationKey && currentDestinationKeyRef.current === destinationKey) {
      // The city may have been disabled after the quote. The order rejection is
      // authoritative, even if an older successful quote finishes afterwards.
      // A delayed rejection for another selected address must not poison this one.
      const city = typeof data.city === 'string' ? data.city : quoteStatus.status === 'ok' ? quoteStatus.city : undefined;
      const sector = typeof data.sector === 'string' ? data.sector : quoteStatus.status === 'ok' ? quoteStatus.sector : undefined;
      setRefusedDestination({ key: destinationKey, city: quoteCityLabel(city, sector) });
      goToStep('step-address');
    }
    if (isTotalMismatch(err)) {
      refreshAfterMismatch();
      if (redemptionActive && redemptionCode) return t('loyalty.checkout.errors.totalMismatch');
    }
    return orderErrorMessage(err, t, lines);
  };

  // B1 — the refusal lands where the customer can act on it: the loyalty step
  // is brought back, marked, and the message is also a toast (the step may have
  // been anywhere else on the wizard).
  const rejectRedemption = (message: string, kind: 'code' | 'small' = 'code') => {
    setRedemptionError(message);
    setLoyaltyRejection(kind);
    goToStep('step-loyalty');
    dispatch(showErrorToast(message));
  };

  const onSubmit = async (serviceChoice?: ServiceChoice) => {
    setServiceChoiceOpen(false);
    setSubmitAttempted(true);
    const validationError = validateBeforePlace();
    if (validationError) {
      refuseBeforeSend(validationError);
      return;
    }
    const scheduledAtIso = isDineIn ? new Date().toISOString() : resolveScheduledAtNow();
    if (!scheduledAtIso) {
      setSlotExpired(true);
      dispatch(showErrorToast(t('checkout.errors.slotUnavailable')));
      return;
    }
    if (isDineIn && serviceQuestion && !serviceChoice) {
      setServiceChoiceOpen(true);
      return;
    }
    try {
      const result = await placeOrder({
        restaurantUuid,
        customer: checkout.customer,
        mode: isDineIn ? 'dine_in' : checkout.mode,
        address: !isDineIn && checkout.mode === 'delivery' ? checkout.address : undefined,
        scheduledAt: scheduledAtIso,
        isAsap: !isDineIn && asapActive ? true : undefined,
        payment: isDineIn ? { channel: 'on_site' } : checkout.payment,
        lines,
        totals: { subtotal, deliveryFee: effectiveDeliveryFee, total: finalTotal },
        cartOptions: answeredCartOptions,
        language: i18n.language || 'fr',
        customerNote: checkout.customerNote,
        guestToken: isDineIn ? tableSession?.guestToken : undefined,
        serviceChoice: isDineIn && serviceQuestion ? serviceChoice : undefined,
        recaptchaToken: recaptchaToken ?? '',
        redemptionCode: redemptionActive ? redemptionCode || undefined : undefined,
        redemptionKnown: appliedAdvantage !== null,
      }).unwrap();
      // SC-346: dine-in rounds merge into one anchor order; pin the just-sent round
      // (highest round_number in the returned lines) so the confirmation recap shows
      // only these dishes, not earlier rounds. DF-0586: plus every service held back.
      const round = isDineIn
        ? sentRoundsParam(result.lines.map((l) => l.roundNumber), result.heldRounds)
        : null;
      finalizeOrder(result.orderId, result.orderAccessToken, round);
    } catch (err) {
      // Turnstile token is single-use; refresh it for a possible retry.
      resetCaptcha();
      reportSendFailure(err);
      // L9-2: a redemption-code rejection surfaces inline next to the code input
      // (not the generic toast) and must not proceed — let the user clear/retry.
      if (redemptionActive && redemptionCode) {
        // The advantage leaves under the card minimum: same reopen-and-mark as a
        // refused code, with the way out (on-site / another advantage) named.
        if (isOnlineAmountTooSmall(err)) {
          rejectRedemption(tooSmallText, 'small');
          return;
        }
        const redemptionMsg = redemptionErrorMessage(err, t);
        if (redemptionMsg) {
          rejectRedemption(redemptionMsg);
          return;
        }
      }
      // Advisory buffet budget gate: cache the server's max_items so the cart
      // disables add/qty beyond the budget for the rest of the session.
      // Server is always authoritative — this is UX-only gating.
      const errData = (err as { data?: { code?: string; max_items?: number; seconds_remaining?: number } } | undefined)?.data;
      if (errData?.code === 'ROUND_BUDGET_EXCEEDED' && errData.max_items) {
        dispatch(setBuffetBudget({ restaurantUuid, maxItems: errData.max_items }));
      }
      // SESSION_EXPIRED clears the table session; ROUND_COOLDOWN starts the CTA countdown
      if (errData?.code === 'SESSION_EXPIRED') {
        dispatch(clearTableSession(restaurantUuid));
      }
      if (errData?.code === 'ROUND_COOLDOWN' && errData.seconds_remaining) {
        setCooldownRemaining(errData.seconds_remaining);
      }
      if (errData?.code === 'DINERS_RATE_LIMIT' && errData.seconds_remaining) {
        setCooldownRemaining(errData.seconds_remaining);
      }
      // SC-316: an expired slot must block validation until a new one is chosen.
      if (isSlotExpiredError(err)) setSlotExpired(true);
      // SC-587: the server named cart lines it refuses — offer the same
      // one-click removal as the drawer instead of a toast the customer can
      // only acknowledge.
      const refused = blockedFromReject(err);
      if (refused.length > 0) {
        setRejectedLines(refused);
        return;
      }
      const message = orderFailureMessage(err);
      setLastSendFailure(message);
      dispatch(showErrorToast(message));
    }
  };

  const placeOrderForCard = async (): Promise<{
    clientSecret?: string;
    orderId: string;
    orderAccessToken?: string | null;
    provider?: string;
    redirect_url?: string;
  } | null> => {
    setSubmitAttempted(true);
    const validationError = validateBeforePlace();
    if (validationError) {
      refuseBeforeSend(validationError);
      return null;
    }
    const scheduledAtIso = isDineIn ? new Date().toISOString() : resolveScheduledAtNow();
    if (!scheduledAtIso) {
      setSlotExpired(true);
      dispatch(showErrorToast(t('checkout.errors.slotUnavailable')));
      return null;
    }
    try {
      const result = await placeOrder({
        restaurantUuid,
        customer: checkout.customer,
        mode: isDineIn ? 'dine_in' : checkout.mode,
        address: !isDineIn && checkout.mode === 'delivery' ? checkout.address : undefined,
        scheduledAt: scheduledAtIso,
        isAsap: !isDineIn && asapActive ? true : undefined,
        payment: isDineIn ? { channel: 'on_site' } : checkout.payment,
        lines,
        totals: { subtotal, deliveryFee: effectiveDeliveryFee, total: finalTotal },
        cartOptions: answeredCartOptions,
        language: i18n.language || 'fr',
        customerNote: checkout.customerNote,
        guestToken: isDineIn ? tableSession?.guestToken : undefined,
        recaptchaToken: recaptchaToken ?? '',
        redemptionCode: redemptionActive ? redemptionCode || undefined : undefined,
        redemptionKnown: appliedAdvantage !== null,
      }).unwrap();
      // Order created (captcha consumed); refresh the token so a payment retry
      // that re-creates via the same idempotency key still carries a valid one.
      resetCaptcha();
      // Stancer: backend returns provider + redirect_url instead of clientSecret
      if (result.provider === 'stancer') {
        return {
          provider: result.provider,
          redirect_url: result.redirectUrl ?? undefined,
          orderId: result.orderId,
          orderAccessToken: result.orderAccessToken,
        };
      }
      if (!result.clientSecret) {
        dispatch(showErrorToast(t('checkout.paymentInitFailed')));
        return null;
      }
      return {
        clientSecret: result.clientSecret,
        orderId: result.orderId,
        orderAccessToken: result.orderAccessToken,
      };
    } catch (err) {
      // Turnstile token is single-use; refresh it for a possible retry.
      resetCaptcha();
      reportSendFailure(err);
      // L9-2: a redemption-code rejection surfaces inline next to the code input
      // (not the generic toast) and must not proceed to payment.
      if (redemptionActive && redemptionCode) {
        if (isOnlineAmountTooSmall(err)) {
          rejectRedemption(tooSmallText, 'small');
          return null;
        }
        const redemptionMsg = redemptionErrorMessage(err, t);
        if (redemptionMsg) {
          rejectRedemption(redemptionMsg);
          return null;
        }
      }
      // SC-316: an expired slot must block validation until a new one is chosen.
      if (isSlotExpiredError(err)) setSlotExpired(true);
      // SC-587: same one-click cart repair as the non-card path above.
      const refused = blockedFromReject(err);
      if (refused.length > 0) {
        setRejectedLines(refused);
        return null;
      }
      const message = orderFailureMessage(err);
      setLastSendFailure(message);
      dispatch(showErrorToast(message));
      return null;
    }
  };

  const cardFormVisible =
    !isDineIn &&
    checkout.payment.channel === 'online' &&
    paymentOnlineEnabled &&
    finalTotal > 0 &&
    !onlineTooSmall &&
    !paymentsBlocked;

  // SC-775 — la barre d'action reste en place même quand le formulaire de carte
  // prend le relais du bouton : elle porte alors le retour et le total, seul
  // moyen de revenir sur une étape précédente.
  // Le bouton d'envoi — et donc le captcha — n'est dans la barre que lorsque le
  // formulaire de carte n'a pas déjà son propre bouton « Payer ». SC-775 : ce
  // formulaire reste monté sur toutes les étapes (le démonter recréerait
  // l'élément Stripe à chaque va-et-vient), donc il ne prend le relais du bouton
  // que lorsque l'étape « Paiement » est bien celle qui est à l'écran — sinon
  // « Suivant » disparaissait de la première étape.
  const cardFormOnScreen = cardFormVisible && currentStepId === 'step-payment';
  const stickyCtaVisible = !cardFormOnScreen;

  // SC-760 — un bouton gris ne dit rien. Tant que quelque chose empêche l'envoi,
  // on nomme l'étape en cause et on propose d'y aller ; le client n'a plus à
  // deviner ce qu'il a sauté. Monté une seule fois, dans la barre d'action.
  // SC-775 : plus rien à expliquer quand le client est DÉJÀ sur l'étape qui
  // bloque — la carte est rouge et le champ fautif porte son propre message. Le
  // rappel ne sert que lorsque le blocage est ailleurs, et il mène à l'étape
  // au lieu d'y faire défiler. Une ligne, pas un bandeau : c'est la barre
  // d'action qui l'héberge.
  const blockerBanner = firstBlocker && firstBlocker.id !== currentStepId ? (
    <div
      role="status"
      data-testid="checkout-blocker"
      className="basis-full flex flex-wrap items-center justify-between gap-x-2 gap-y-1 border-2 border-ink rounded-btn bg-ink px-2 py-1 font-display text-xs leading-snug text-paper"
    >
      <span id="checkout-blocker-reason">
        <span aria-hidden="true">⚠</span>{' '}
        {t('checkout.todo.banner', { label: firstBlocker.label })}
      </span>
      <button
        type="button"
        onClick={() =>
          steps.some((s) => s.id === firstBlocker.id)
            ? goToStep(firstBlocker.id)
            : scrollToStep(firstBlocker.id)
        }
        data-testid="checkout-blocker-jump"
        className="flex-none cursor-pointer rounded-btn-sm border-2 border-solid border-paper bg-transparent px-2 py-[2px] font-mono text-[11px] text-paper"
      >
        {t('checkout.todo.cta')}
      </button>
    </div>
  ) : null;

  // SC-760: mounted only once the customer has filled what the order needs from
  // them. A Turnstile token expires after a few minutes — issued the second the
  // page opens, it was routinely dead by the time the form was finished, and the
  // customer got « la vérification n'est pas terminée » on a form they had just
  // completed. Same reason `onExpire` re-arms the widget instead of only
  // dropping the token. `validateBeforePlace` checks the fields first, so an
  // unmounted widget can never be what a customer is told to wait for.
  // SC-775 (retour QA) : la vérification n'apparaît QUE sur la dernière étape,
  // « Paiement » — celle qui porte le bouton d'envoi. Elle se montait dès que
  // les coordonnées étaient remplies, donc en plein milieu de l'assistant, sur
  // une étape qui n'envoie rien. `needCaptcha` exclut le sur place, et le mode
  // en ligne finit toujours par l'étape « Paiement » : dernière étape =
  // `step-payment`. Bonus : le jeton est émis au plus tard, donc au plus frais.
  const captchaShown = needCaptcha && isLastStep && !contactIncomplete && !addressIncomplete;
  // DF-0419 — each mount is a fresh attempt: its help starts blank, and the
  // diagnostic log gets the « mounted » that its other events are counted against.
  useEffect(() => {
    if (!captchaShown) return;
    captchaMountedAtRef.current = Date.now();
    setCaptchaIssue(null);
    setCaptchaBlockedClick(false);
    reportCaptcha('mounted');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captchaShown]);
  const captchaEl = captchaShown ? (
    <TurnstileWidget
      ref={turnstileRef}
      siteKey={recaptchaSiteKey}
      onSuccess={(token) => {
        reportCaptcha('token');
        setRecaptchaToken(token);
        setCaptchaBroken(false);
        setCaptchaIssue(null);
        setCaptchaBlockedClick(false);
      }}
      onExpire={() => { setRecaptchaToken(null); turnstileRef.current?.reset(); }}
      onError={() => { reportCaptcha('error'); setRecaptchaToken(null); setCaptchaBroken(true); }}
      onStuck={(reason) => { reportCaptcha(reason); setCaptchaIssue('stuck'); }}
      onInteractive={(interactive) => {
        if (interactive) reportCaptcha('interactive');
        setCaptchaIssue((issue) => (interactive ? 'interactive' : issue === 'interactive' ? null : issue));
      }}
    />
  ) : null;

  return (
    // `scrollMarginTop` : la page remonte à chaque étape, et sans marge le titre
    // et le haut du bandeau passaient sous l'en-tête collant du site.
    // `min-h` + l'espaceur avant le pied : la barre d'action reste au bas de
    // l'écran sur chaque étape, au lieu de suivre la hauteur de l'étape (le
    // bouton « Suivant » sautait de 530 à 757 px d'un écran à l'autre).
    <div
      ref={pageTopRef}
      style={{ scrollMarginTop: STEP_SCROLL_MARGIN }}
      className="checkout-scope max-w-[800px] mx-auto px-4 pt-5 space-y-4 flex flex-col min-h-[calc(100svh-var(--site-header-h,110px))]"
    >
      <h1 className="font-display text-2xl sm:text-3xl">
        {isDineIn ? t('checkout.dineIn.recapTitle') : t('checkout.title')}
      </h1>

      {/* Une invitation au compte par écran, pas plus : le bandeau n'est que sur
          la première étape, et jamais sur « Coordonnées » qui porte la sienne
          (quand un seul mode est offert, « Coordonnées » EST la première étape).
          Il se répétait au-dessus de chaque étape de l'assistant, et « Coordonnées »
          empilait bandeau + intro + invitation : six fois « compte » sur un écran. */}
      {guestBannerVisible && guestNudgeInfo && guestPromptStep && (
        <GuestPointsBanner
          nudge={guestNudgeInfo}
          onLogin={openLoginFromCheckout}
          onRegister={openRegisterFromCheckout}
          onDismiss={dismissGuestBanner}
        />
      )}

      {/* SC-760 — le fil des étapes : ce qui reste à faire se voit sans avoir à
          descendre jusqu'au bouton, et chaque pastille y ramène. */}
      <StepRail steps={steps} onJump={goToStep} activeId={currentStepId} />

      {priceChangedNames.length > 0 && (
        <div
          role="status"
          className="border-2 border-ink bg-cream p-3 font-mono text-sm shadow-stamp"
          data-testid="price-change-banner"
        >
          {t('checkout.priceUpdated', { names: priceChangedNames.join(', ') })}
        </div>
      )}

      {/* SC-879 : l'horloge a poussé le créneau plus loin (SC-568). L'avis vivait dans la
          carte « Horaire », donc `hidden` dès que le client était passé à
          l'étape suivante de l'assistant (SC-775) : il payait pour 12:24 en
          croyant toujours avoir 12:19. Au niveau de la page, il se lit depuis
          n'importe quelle étape, et le raccourci ramène au sélecteur. */}
      {!isDineIn && !asapActive && slotAdvancedFrom && !slotExpired && activeSlots.length > 0 && (
        <div
          role="status"
          className="flex flex-wrap items-center gap-2 border-2 border-ink bg-cream p-3 font-mono text-sm shadow-stamp"
          data-testid="slot-advanced-notice"
        >
          <span className="min-w-0 flex-1">
            ⚠ {t('checkout.slotAdvanced', { from: slotAdvancedFrom, to: selectedSlot })}
          </span>
          {currentStepId !== 'step-time' && (
            <button
              type="button"
              onClick={() => goToStep('step-time')}
              data-testid="slot-advanced-goto"
              className="flex-none cursor-pointer rounded-btn-sm border-2 border-solid border-ink bg-paper px-2 py-[2px] font-mono text-[11px] text-ink"
            >
              {t('checkout.todo.cta')}
            </button>
          )}
        </div>
      )}

      {/* SC-335 / SC-587: proactive availability warning — name the cart line(s)
          no longer orderable and let the customer drop them all in one click,
          which is the only way forward: the order cannot go out with them. */}
      {hasUnavailableLines && (
        <div
          role="alert"
          id="checkout-cart-alert"
          style={{ scrollMarginTop: STEP_SCROLL_MARGIN }}
          className="border-2 border-sale-red bg-cream p-3 font-mono text-sm shadow-stamp space-y-2"
          data-testid="unavailable-lines-banner"
        >
          <p className="m-0">
            {t('checkout.errors.productUnavailableNamed', { products: unavailableLineNames.join(', ') })}
          </p>
          <button
            type="button"
            onClick={() => dropBlockedLines(blockedLines)}
            className="w-full bg-hot-pink text-[var(--on-accent)] border-2 border-ink rounded-btn py-2 font-display"
            data-testid="unavailable-lines-remove-all"
          >
            {t('cart.blocked.removeAll')}
          </button>
        </div>
      )}

      {/* La livraison retirée en cours de route, et le minimum de commande non
          atteint : deux messages qui vivaient dans la carte « Mode », donc
          illisibles dès l'étape suivante — et carrément invisibles depuis que
          cette carte s'efface quand le restaurant n'offre qu'un mode. Au niveau
          de la page, ils se lisent depuis n'importe quelle étape, comme le
          rappel de créneau au-dessus. */}
      {!isDineIn && deliveryDropped && (
        <p
          role="status"
          data-testid="delivery-unavailable-banner"
          className="border-2 border-ink bg-cream p-3 font-mono text-sm shadow-stamp"
        >
          {t('checkout.deliveryUnavailable')}
        </p>
      )}
      {!isDineIn && belowOrderMin && (
        <p
          role="status"
          id="checkout-order-min"
          data-testid="order-min-warning"
          style={{ scrollMarginTop: STEP_SCROLL_MARGIN }}
          className="border-2 border-solid border-sale-red bg-cream p-3 font-mono text-sm shadow-stamp"
        >
          {t(
            orderMode === 'livraison' &&
              quoteStatus.status === 'ok' &&
              quoteStatus.city &&
              quoteStatus.min_order_ttc != null
              ? 'cart.belowMinimumCity'
              : 'cart.belowMinimum',
            {
              city:
                quoteStatus.status === 'ok'
                  ? quoteCityLabel(quoteStatus.city, quoteStatus.sector)
                  : undefined,
              minimum: (minOrderCents / 100).toFixed(2).replace('.', ','),
              missing: (orderMinMissingCents / 100).toFixed(2).replace('.', ','),
            },
          )}
        </p>
      )}

      {/* SC-775 : cantonnée à la première étape. C'est une invitation d'entrée
          de jeu ; répétée au-dessus de chaque étape de l'assistant, elle prend
          la place de l'étape elle-même. Pas sur « Coordonnées » (même raison que
          le bandeau), ni après que le client a fermé le bandeau : fermer une
          invitation pour en voir surgir une autre, c'est ne pas avoir été écouté. */}
      {guestPromptStep && !guestBannerVisible && !guestBannerDismissed && (
        <button
          type="button"
          data-testid="checkout-soft-login"
          onClick={() => dispatch(openAccountModal({
            mode: 'login',
            redirectTo: location.pathname,
          }))}
          className="block w-full text-start bg-cream border-2 border-ink rounded-btn-sm px-4 py-3 text-ink underline hover:-translate-y-[1px] hover:-translate-x-[1px] transition-transform"
        >
          {t('account.checkout.softPrompt')}
        </button>
      )}

      {/* Step 1: Mode — hidden for dine_in, et absente quand un seul mode est
          possible : la carte n'aurait plus qu'un bouton déjà retenu. */}
      {!isDineIn && steps.some((s) => s.id === 'step-mode') && (
        <StepCard {...stepProps('step-mode')}>
          {modesResolved && allowedModes.length === 0 ? (
            <p className="font-mono text-sm text-ink/70">
              {t('restaurant.noOnlineOrders')}
            </p>
          ) : (
            <div className="flex gap-3">
              {allowedModes.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    dispatch(setMode(m));
                    // SC-1092 : la carte et le panier affichent les prix du mode
                    // du panier ; sans ce report, revenir a la carte montrait
                    // ceux de l'ancien mode.
                    dispatch(setCartMode({ restaurantUuid, mode: m }));
                  }}
                  className={`flex-1 py-3 border-2 border-ink rounded-btn font-display ${
                    checkout.mode === m ? 'bg-mint' : 'bg-paper'
                  }`}
                >
                  {t(m === 'delivery' ? 'order.modeDelivery' : 'order.modeTakeaway')}
                </button>
              ))}
            </div>
          )}
        </StepCard>
      )}

      {/* Step 2: Address (if delivery) — Google Places autocomplete + server quote */}
      {!isDineIn && checkout.mode === 'delivery' && (
        <StepCard {...stepProps('step-address')} sectionRef={addressSectionRef}>
          {attempted('step-address') && addressIncomplete && (
            <p className="mb-2 font-mono text-sm text-sale-red">
              {addressFieldsComplete
                ? `⚠ ${deliveryQuoteError()}`
                : t('checkout.addressIncompleteWarning')}
            </p>
          )}
          <DeliveryStep
            address={checkout.address}
            onAddressChange={(a) => {
              if (a.place_id !== checkout.address.place_id) scrollToQuoteRef.current = true;
              dispatch(setAddress(a));
            }}
            quote={quoteStatus}
            panelRef={quotePanelRef}
            // SC-1205 — le bandeau du minimum est en haut de page ; à cette
            // étape le client lit le devis, plus bas, et ne le voyait pas. On
            // le redit sous le devis, dans le bloc que le défilement de SC-1155
            // ramène à l'écran. Sans `role` : le bandeau l'annonce déjà.
            quoteNotice={
              quoteStatus.status === 'ok' && belowOrderMin ? (
                <p
                  data-testid="order-min-under-quote"
                  className="border-2 border-solid border-sale-red bg-cream p-3 font-mono text-sm"
                >
                  <strong>
                    ⚠{' '}
                    {t('cart.belowMinimum', {
                      minimum: (minOrderCents / 100).toFixed(2).replace('.', ','),
                      missing: (orderMinMissingCents / 100).toFixed(2).replace('.', ','),
                    })}
                  </strong>
                </p>
              ) : undefined
            }
            servedCities={servedCities}
            triggerQuote={handleDeliveryStepQuote}
            onRetry={handleRetryQuote}
            savedAddresses={savedAddresses}
            onPickSaved={(a) => {
              if (a.place_id !== checkout.address.place_id) scrollToQuoteRef.current = true;
              dispatch(setAddress({
                street: a.street,
                city: a.city,
                zip: a.zip,
                instructions: a.instructions ?? '',
                place_id: a.place_id,
                lat: a.lat,
                lng: a.lng,
                formatted_address: a.formatted_address,
              }));
            }}
            onRemoveSaved={isAuthed ? (id) => dispatch(removeAddress({ id })) : undefined}
          />
        </StepCard>
      )}

      {/* Step 3: Coordonnées — hidden for dine_in (seated guest, no contact
          details required; the order is tied to the table via the guest token) */}
      {!isDineIn && (
      <StepCard {...stepProps('step-customer')}>
        {/* Sans preflight, un <input> garde 13,33 px — iOS zoome alors sur la
            page au focus — et 37 px de haut. `type`/`autoComplete` : clavier
            numérique pour le téléphone, remplissage automatique du navigateur. */}
        <div className="grid sm:grid-cols-2 gap-2.5">
          <div>
            <input
              ref={nameRef}
              name="name"
              autoComplete="name"
              className={`min-h-[44px] border-2 border-solid px-2 font-mono text-base w-full ${customerAttempted && nameInvalid ? 'border-sale-red' : 'border-ink'}`}
              placeholder={t('reservation.namePlaceholder')}
              maxLength={CUSTOMER_NAME_MAX}
              value={checkout.customer.name}
              onChange={(e) => dispatch(setCustomer({ name: e.target.value }))}
            />
            {customerAttempted && nameInvalid && (
              <p className="mt-1 font-mono text-xs text-sale-red">⚠ {t('reservation.invalidName')}</p>
            )}
          </div>
          <div>
            <input
              ref={phoneRef}
              type="tel"
              name="tel"
              autoComplete="tel"
              inputMode="tel"
              className={`min-h-[44px] border-2 border-solid px-2 font-mono text-base w-full ${customerAttempted && phoneInvalid ? 'border-sale-red' : 'border-ink'}`}
              placeholder={t('reservation.phonePlaceholder')}
              maxLength={CUSTOMER_PHONE_MAX}
              value={checkout.customer.phone}
              onChange={(e) => dispatch(setCustomer({ phone: e.target.value }))}
            />
            {customerAttempted && phoneInvalid ? (
              <p className="mt-1 font-mono text-xs text-sale-red">⚠ {t('reservation.invalidPhone')}</p>
            ) : phoneMissing && (
              <p className="mt-1 font-mono text-xs text-ink/70" data-testid="checkout-phone-saved-hint">
                {t('checkout.phoneSavedToAccount')}
              </p>
            )}
          </div>
          {isAuthed && currentUser?.email ? (
            <p className="col-span-2 font-mono text-sm text-ink/70">
              {t('account.modal.fields.email')}&nbsp;:{' '}
              <span className="font-semibold text-ink">{currentUser.email}</span>{' '}
              <span className="text-ink/50">{t('checkout.connectedAccount')}</span>
            </p>
          ) : (
            <div className="col-span-2">
              <input
                ref={emailRef}
                type="email"
                name="email"
                autoComplete="email"
                  required
                className={`min-h-[44px] border-2 border-solid px-2 font-mono text-base w-full ${customerAttempted && emailInvalid ? 'border-sale-red' : 'border-ink'}`}
                placeholder={t('reservation.emailPlaceholder')}
                value={checkout.customer.email}
                onChange={(e) => dispatch(setCustomer({ email: e.target.value }))}
              />
              {customerAttempted && emailInvalid && (
                <p className="mt-1 font-mono text-xs text-sale-red">⚠ {t('reservation.invalidEmail')}</p>
              )}
            </div>
          )}
        </div>
        {/* SC-1013 — la proposition de compte est ici, et non en fin de
            parcours : le client vient de taper son nom, son e-mail et son
            téléphone, c'est le seul endroit où créer un compte ne lui coûte
            plus qu'un mot de passe. Elle ne prend pas d'écran à elle seule —
            « Suivant » poursuit sans compte. */}
        {!isAuthed && (
          <AccountInvite
            onCreateAccount={openRegisterFromCheckout}
            onLogin={openLoginFromCheckout}
            nudge={guestNudgeInfo}
          />
        )}
      </StepCard>
      )}

      {/* Step 4: Horaire — hidden for dine_in */}
      {!isDineIn && (
        <StepCard {...stepProps('step-time')}>
          {/* SC-1090 — day toggle, Aujourd'hui..J+N. Quand preorderMaxDays === 1
              (horizon d'avant ce ticket) ce rendu est identique à l'ancien
              toggle à deux boutons : même conteneur, mêmes testids, même
              texte. Au-delà, une grille : tous les jours restent visibles (une
              rangée qui défile cachait les jours au-delà du 3e sur mobile). */}
          <div
            className={preorderMaxDays > 1 ? 'grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3' : 'flex gap-2 mb-3'}
            data-testid="day-toggle"
          >
            {Array.from({ length: preorderMaxDays + 1 }, (_, offset) => offset).map((offset) => {
              const data = dayData[offset] ?? { daySlots: [], slots: [] };
              // Un jour sans aucun creneau se verrouille, comme l'onglet Demain
              // le faisait deja : sinon le client peut revenir sur un jour sans
              // creneau, y lire « Aucun creneau disponible » et rebloquer l'etape.
              const disabled = data.slots.length === 0 && data.daySlots.length === 0;
              const testId = offset === 0 ? 'day-toggle-today' : offset === 1 ? 'day-toggle-tomorrow' : `day-toggle-d${offset}`;
              const label = offset === 0
                ? t('checkout.dayToday')
                : offset === 1
                  ? t('checkout.dayTomorrow')
                  : weekdayNameForOffset(now, offset, i18n.language, identity);
              const dateLabel = offset === 0
                ? todayDayLabel
                : offset === 1
                  ? tomorrowDayLabel
                  : dayLabelForOffset(now, offset, i18n.language, identity);
              const dateTestId = `${testId}-date`;
              return (
                <button
                  key={offset}
                  type="button"
                  onClick={() => { setSelectedDay(offset); setAsapPicked(true); dispatch(setScheduledAt('')); }}
                  // Un jour ferme doit se lire ferme : la bordure seule plus claire
                  // le laissait passer pour un jour simplement non choisi.
                  className={`${preorderMaxDays > 1 ? '' : 'flex-1 '}border-2 rounded-btn px-2 py-1.5 font-mono text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    selectedDay === offset
                      ? 'border-ink bg-ink text-paper'
                      : 'border-ink/30 bg-paper text-ink'
                  }`}
                  disabled={disabled}
                  data-testid={testId}
                >
                  <span className="block">{label}</span>
                  <span className="block text-xs opacity-80" data-testid={dateTestId}>
                    {dateLabel}
                  </span>
                </button>
              );
            })}
          </div>
          {activeSlots.length === 0 ? (
            <p className="font-mono text-sm text-ink/60" data-testid="no-slots-notice">
              {t(
                activeDaySlots.length > 0
                  ? 'checkout.noSlotsCartRestricted'
                  : selectedDay === 0
                    ? 'checkout.noSlots'
                    : selectedDay === 1
                      ? 'checkout.noSlotsTomorrow'
                      : 'checkout.noSlotsDay',
                selectedDay >= 2 ? { day: dayLabelForOffset(now, selectedDay, i18n.language, identity) } : undefined,
              )}
            </p>
          ) : (
            <select
              value={asapActive ? ASAP_OPTION : selectedSlot}
              onChange={(e) => {
                if (e.target.value === ASAP_OPTION) {
                  setAsapPicked(true);
                  // Le créneau expiré ne compte plus : l'envoi recalcule le plus proche.
                  setSlotExpired(false);
                  setSlotAdvancedFrom(null);
                  return;
                }
                setAsapPicked(false);
                slotPickedRef.current = true;
                dispatch(setScheduledAt(e.target.value));
                setSlotAdvancedFrom(null);
              }}
              className="border-2 border-ink rounded-btn font-mono text-sm py-2 px-3 bg-paper"
            >
              {asapOffered && <option value={ASAP_OPTION}>{t('checkout.slotAsap')}</option>}
              {activeSlots.map((s, i) => (
                <option key={s} value={s}>
                  {selectedDay === 0 && i === 0 ? t('checkout.slotEarliest', { time: s }) : s}
                </option>
              ))}
            </select>
          )}
          {slotExpired && (
            <p
              className="mt-3 font-mono text-sm text-sale-red"
              role="alert"
              data-testid="slot-expired-warning"
            >
              {t('checkout.errors.slotUnavailable')}
            </p>
          )}
        </StepCard>
      )}

      {/* SC-197: dine-in order recap — let the seated guest verify products,
          quantities, options and line prices without going back to the cart. */}
      {isDineIn && (
        <StepCard {...stepProps('step-recap')} testId="dine-in-order-recap">
          <div>
            {lines.map((line) => (
              <CartLineItem
                key={line.lineId}
                line={line}
                restaurantUuid={restaurantUuid}
                optionGroups={menu?.optionGroups ?? []}
                restaurantLogoUrl={identity?.logoUrl ?? null}
                restaurantName={identity?.name ?? ''}
                themeColor={identity?.theme?.primary ?? '#1f2937'}
                blockReason={blockedLines.find((b) => b.lineId === line.lineId)?.reason}
              />
            ))}
          </div>
        </StepCard>
      )}

      {/* SC-739 — questions posées sur la commande entière, juste avant la note :
          le client finit sa commande d'un seul geste plutôt que de revenir sur
          chaque plat. SC-760 : c'est une étape numérotée comme les autres, et
          non plus un encadré sans rang que l'œil saute. */}
      {cartOptionGroups.length > 0 && (
        <CartOptionsSection
          {...stepProps('step-options')}
          groups={cartOptionGroups}
          selections={checkout.cartOptions}
          onChange={(selection) => dispatch(setCartOption(selection))}
          showMissing={attempted('step-options')}
        />
      )}

      {/* Step: Note (optional) */}
      <StepCard {...stepProps('step-note')}>
        <NoteField
          className="w-full border-2 border-ink p-2 font-mono text-sm bg-paper resize-none"
          counterClassName="mt-1 text-end font-mono text-xs text-ink/60"
          rows={3}
          maxLength={280}
          placeholder={
            checkout.mode === 'delivery'
              ? t('cart.notePlaceholderDelivery')
              : t('cart.notePlaceholderTakeaway')
          }
          value={checkout.customerNote}
          onCommit={(next) => dispatch(setCustomerNote(next))}
        />
      </StepCard>

      {/* L9-2 / SC-1091: loyalty advantages — only for a logged-in customer at a
          restaurant with an active loyalty program. One tap selects a coupon (or
          unlocks a reward first); the manual code stays behind « J'ai un code ».
          The discount is estimated here and computed server-side: the summary
          keeps showing the pre-redemption total. */}
      {redemptionActive && (
        <StepCard {...stepProps('step-loyalty')} testId="loyalty-redemption-section">
          <LoyaltyRedemption
            restaurantUuid={restaurantUuid}
            goodsCents={goodsAfterPromo}
            tz={tz}
            code={redemptionCode}
            onCodeChange={setRedemptionCode}
            error={redemptionError}
            onError={handleRedemptionError}
            finalTotalCents={finalTotal}
            earnPoints={earnPoints}
            earnAtCounter={earnAtCounter}
            totalCents={total}
            onlineBlocked={checkout.payment.channel === 'online' && !paymentOnsiteEnabled}
            onlineTooSmall={onlineTooSmall}
            canPayOnsite={paymentOnsiteEnabled}
            onPayOnsite={payOnsiteInstead}
            onProgramDisabled={handleProgramDisabled}
            onSelectionChange={setLoyaltySelection}
            programOn={programOn}
          />
        </StepCard>
      )}

      {/* Step 6: Payment — hidden for dine_in (always on_site) */}
      {!isDineIn && (
        <StepCard {...stepProps('step-payment')}>
          {/* SC-1091 M3 — the advantage the customer picked two steps ago is
              named here, where they pay; « Modifier » goes back to it. */}
          {((appliedAdvantage && redemptionCents > 0) || codeValueUnknown) && (
            <div
              data-testid="payment-loyalty-recap"
              className="mb-3 flex items-center gap-2 rounded-btn-sm border-2 border-solid border-ink bg-cream px-3 py-2 font-mono text-sm"
            >
              <span className="min-w-0 flex-1">
                <span aria-hidden="true">🎁 </span>
                {appliedAdvantage ? (
                  <>
                    {t('loyalty.checkout.payRecap')} <bdi dir="auto">{appliedAdvantage.name}</bdi> ·{' '}
                    <bdi dir="ltr">
                      −<Price amountCents={redemptionCents} className="font-normal" />
                    </bdi>
                  </>
                ) : (
                  t('loyalty.checkout.payRecapUnknown')
                )}
              </span>
              <button
                type="button"
                onClick={() => goToStep('step-loyalty')}
                data-testid="payment-loyalty-change"
                className="min-h-[44px] flex-none border-0 bg-transparent p-0 font-mono text-sm text-ink underline"
              >
                {t('loyalty.checkout.payRecapChange')}
              </button>
            </div>
          )}
          {onlineTooSmall && (
            <div className="mb-3">
              <OnlineTooSmallNotice canPayOnsite={paymentOnsiteEnabled} onPayOnsite={payOnsiteInstead} where="payment" />
            </div>
          )}
          {paymentsEmpty && (
            <p className="font-mono text-sm text-ink/70">
              {t('checkout.paymentsEmpty')}
            </p>
          )}
          {!paymentsBlocked && (
            <div className="space-y-2">
              {paymentOnlineEnabled && (
                <label
                  className={`flex items-center gap-3 px-3 py-2 border-2 border-ink font-mono cursor-pointer ${
                    checkout.payment.channel === 'online' ? 'bg-mint' : 'bg-paper'
                  }`}
                >
                  <input
                    type="radio"
                    name="payment-channel"
                    checked={checkout.payment.channel === 'online'}
                    onChange={() => dispatch(setChannel('online'))}
                  />
                  <span>
                    💳 {t('checkout.payOnline')}
                    <br />
                    <span className="text-xs text-ink/60 whitespace-pre-line">
                      {paymentOnlineDescription || t('checkout.payOnlineHint')}
                    </span>
                  </span>
                </label>
              )}
              {paymentOnsiteEnabled && (
                <label
                  className={`flex items-center gap-3 px-3 py-2 border-2 border-ink font-mono cursor-pointer ${
                    checkout.payment.channel === 'on_site' ? 'bg-mint' : 'bg-paper'
                  }`}
                >
                  <input
                    type="radio"
                    name="payment-channel"
                    checked={checkout.payment.channel === 'on_site'}
                    onChange={() => dispatch(setChannel('on_site'))}
                  />
                  <span>
                    🏪 {t('checkout.payOnsite')}
                    <br />
                    <span className="text-xs text-ink/60 whitespace-pre-line">
                      {paymentOnsiteDescription || t('checkout.payOnsiteHint')}
                    </span>
                  </span>
                </label>
              )}
              {earnPoints > 0 && (
                <p data-testid="payment-earn-estimate" className="m-0 font-mono text-sm text-ink">
                  <span aria-hidden="true">🎁 </span>
                  {t('loyalty.checkout.earn', { n: earnPoints })}
                </p>
              )}
              {earnAtCounter && (
                <p data-testid="payment-earn-counter" className="m-0 font-mono text-sm text-ink">
                  <span aria-hidden="true">🎁 </span>
                  {t('loyalty.checkout.earnCounter')}
                </p>
              )}
              {cardFormVisible && (
                <div className="mt-3 p-3 border-2 border-ink bg-cream">
                  {checkoutConfigFailed ? (
                    <p
                      role="alert"
                      className="font-mono text-sm text-sale-red"
                      data-testid="payment-form-unavailable"
                    >
                      ⚠ {t('checkout.errors.paymentFormUnavailable')}
                    </p>
                  ) : activeProvider === 'stancer' ? (
                    <StancerRedirectSection onPlaceOrder={placeOrderForCard} disabled={submitDisabled || slotExpired || hasUnavailableLines} />
                  ) : (
                    <PaymentElementSection
                      restaurantUuid={restaurantUuid}
                      totalCents={finalTotal}
                      currency="eur"
                      placeOrder={placeOrderForCard as () => Promise<{ clientSecret: string; orderId: string; orderAccessToken?: string | null } | null>}
                      buildReturnUrl={(orderId, token) => `${window.location.origin}${confirmationUrl(orderId, token)}`}
                      onSuccess={(orderId, token) => finalizeOrder(orderId, token)}
                      onError={(message) => dispatch(showErrorToast(message))}
                      disabled={submitDisabled || slotExpired || hasUnavailableLines}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </StepCard>
      )}

      {/* SC-775 — les relances d'offre quittent la barre d'action, où elles
          empilaient jusqu'à trois lignes au-dessus du bouton. Elles restent
          visibles : la page n'affiche plus qu'une étape, donc elle tient dans
          l'écran, et le bandeau rouge se lit mieux en pleine largeur qu'écrasé
          à 360 px contre le bouton. */}
      {!isDineIn && promoHint && (
        <UpsellBanner testId="checkout-promo-hint">
          {t('checkout.promoHint', {
            amount: (promoHint.gapCents / 100).toFixed(2).replace('.', ','),
            percent: promoHint.rule.discountPercent,
          })}
        </UpsellBanner>
      )}
      {cartOptionHint && (
        <UpsellBanner testId="cart-option-hint">
          {t('checkout.cartOptionHint', {
            amount: (cartOptionHint.gapCents / 100).toFixed(2).replace('.', ','),
            label: cartOptionHint.group.label,
          })}
        </UpsellBanner>
      )}

      {/* SC-197 §5: the no-cancellation / call-the-restaurant notice is for
          takeaway & delivery; it has no value for a seated dine-in guest.
          SC-775 : réservé à la dernière étape — c'est un avertissement d'avant
          l'envoi, pas une mention à porter sur chaque écran de l'assistant. */}
      {!isDineIn && isLastStep && <CancellationPolicyNotice restaurantUuid={restaurantUuid} />}

      {/* SC-817 — le pied de page collant : la vérification anti-robot, puis la
          barre d'action.

          SC-315 puis SC-775 l'avaient rendue au fil de la page, juste au-dessus
          de la barre — mais la barre, elle, est collée au bas de la fenêtre.
          Sur l'étape « Paiement » le widget passait donc sous la ligne de
          flottaison : le client voyait « Passer la commande » sans voir la
          vérification qui le conditionne, cliquait, lisait « la vérification
          n'est pas terminée » et n'avait aucune raison de descendre la
          chercher. Elle suit désormais le bouton qu'elle garde.

          Elle ne revient pas DANS la barre pour autant (retour QA de SC-775 :
          la carte fait 300 px, dans une barre de 358 px elle écrasait le total
          et repoussait le bouton) — elle a sa propre ligne, au-dessus, et la
          barre garde « retour, total, bouton ».

          Un seul point de montage, hors de la barre : le formulaire de carte
          (qui a son propre bouton « Payer ») y trouve le jeton comme le bouton
          d'envoi. */}
      <div aria-hidden="true" className="flex-1" />
      <div className="sticky bottom-0 z-[410]">
        {captchaEl && (
          <div
            data-testid="checkout-captcha"
            className="flex flex-wrap justify-center gap-y-2 border-2 border-b-0 border-ink bg-paper px-2 py-2"
          >
            {captchaEl}
            <CaptchaHelp hint={captchaHint} phone={identity?.phone ?? null} onRetry={retryCaptcha} />
          </div>
        )}

        {/* SC-775 — la barre d'action tient sur une seule ligne : retour, total,
            bouton. Ce qui n'est pas l'action (relances d'offre, retour à la table)
            est remonté dans le corps de la page ; la remise se fond dans la ligne
            du total au lieu de lui voler une ligne entière.

            Le bouton change de rôle avec l'étape : « Suivant » tant qu'il en reste
            une, l'envoi sur la dernière. « Suivant » n'est jamais grisé — un
            bouton mort n'explique rien —, il refuse d'avancer et met le champ
            fautif au rouge (voir `onNextStep`). */}
        <div
          data-testid="checkout-action-bar"
          className="flex flex-wrap items-center gap-x-2 gap-y-2 border-2 border-ink bg-paper px-2 py-2 shadow-stamp"
        >
          {blockerBanner}
          {/* Le retour de l'assistant. Sur la première étape il n'y a pas
              d'étape précédente : la flèche ramène là d'où vient le client — sa
              table en mode sur place (Fix 10 / SC-197 §6), le menu sinon. */}
          {currentIndex > 0 ? (
            <button
              type="button"
              onClick={() => goToStep(steps[currentIndex - 1].id)}
              data-testid="checkout-step-back"
              aria-label={t('checkout.steps.back')}
              className="flex-none bg-paper border-2 border-ink rounded-btn px-3 py-2 font-display shadow-stamp-sm"
            >
              <span aria-hidden="true" className="inline-block rtl:-scale-x-100">←</span>
            </button>
          ) : isDineIn ? (
            <Link
              to={`/${restaurantUuid}/table/${tableSession?.tableToken ?? ''}`}
              className="flex-none inline-flex items-center gap-1 bg-paper border-2 border-ink rounded-btn px-2 py-2 font-display text-xs shadow-stamp-sm hover:-translate-y-[1px] hover:-translate-x-[1px] transition-transform"
              data-testid="dine-in-back-to-table"
            >
              <span aria-hidden="true" className="inline-block rtl:-scale-x-100">←</span> {t('checkout.dineIn.retourTable')}
            </Link>
          ) : (
            <Link
              to={`/${restaurantUuid}`}
              data-testid="checkout-back-to-menu"
              aria-label={t('checkout.steps.backToMenu')}
              className="flex-none bg-paper border-2 border-ink rounded-btn px-3 py-2 font-display shadow-stamp-sm"
            >
              <span aria-hidden="true" className="inline-block rtl:-scale-x-100">←</span>
            </Link>
          )}
          {/* SC-1048 — jamais plus étroite que son plus long morceau insécable
              (le total, la puce de remise) : quand ça ne tient plus — 320 px,
              « Passer la commande », total à 4 chiffres — c'est le bouton qui
              passe à la ligne, au lieu de se poser sur le montant. */}
          <div className="min-w-min flex-1 leading-tight">
            <span className="font-mono text-xs text-ink/60">{t('order.total')}</span>{' '}
            <Price amountCents={finalTotal} className="text-lg" />
            {/* Fix 2: hide promo row for dine-in */}
            {/* SC-1048 — l'espace est le seul point de coupure entre le prix et
                la puce (tous deux nowrap) : sans lui, les deux passaient sous le
                bouton au lieu d'aller à la ligne. */}
            {!isDineIn && discount > 0 && (
              <>
                {' '}
                <span
                  className="ms-1.5 whitespace-nowrap font-mono text-xs text-hot-pink"
                  data-testid="checkout-discount"
                >
                  🎁 <Price amountCents={-discount} />
                </span>
              </>
            )}
            {redemptionCents > 0 && (
              <>
                {' '}
                <span
                  className="ms-1.5 whitespace-nowrap font-mono text-xs text-hot-pink"
                  data-testid="checkout-loyalty-discount"
                >
                  🎁 <Price amountCents={-redemptionCents} />
                </span>
              </>
            )}
          </div>
          {/* Fix 14: disable CTA and show countdown while ROUND_COOLDOWN is active */}
          {stickyCtaVisible && (
            <button
              type="button"
              onClick={isLastStep ? () => onSubmit() : onNextStep}
              disabled={
                isLastStep &&
                (submitDisabled || cooldownRemaining > 0 || slotExpired || hasUnavailableLines)
              }
              aria-describedby={firstBlocker ? 'checkout-blocker-reason' : undefined}
              data-testid={isLastStep ? 'checkout-submit' : 'checkout-next-step'}
              className="flex-none bg-mint border-2 border-ink rounded-btn px-5 py-3 font-display shadow-stamp-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              {!isLastStep
                ? t('checkout.steps.next')
                : isLoading
                ? '...'
                : cooldownRemaining > 0
                ? t('checkout.errors.roundCooldown', { seconds: cooldownRemaining })
                : isDineIn
                ? t('checkout.dineIn.finalize')
                : t('checkout.placeOrder')}
            </button>
          )}
          {/* SC-879 — l'heure retenue, sous les yeux du client à chaque étape. Elle ne
              s'affichait que dans le sélecteur de la carte « Horaire », donc
              nulle part une fois cette étape quittée : un créneau avancé par
              l'horloge (SC-568) partait sans avoir jamais été lu.
              SC-1048 — elle a sa propre ligne, sur toute la largeur de la barre.
              En incise du total elle partageait la colonne étroite que laisse
              le bouton (≈ 85 px sous « Passer la commande » à 360 px) et passait
              dessous dès qu'une remise allongeait la ligne. */}
          {!isDineIn && selectedSlot && (
            <span
              className="basis-full whitespace-nowrap font-mono text-xs text-ink/70"
              data-testid="checkout-selected-slot"
            >
              {/* « Retrait a 12:30 » seul se lit « tout a l'heure » : sur une
                  pre-commande J+N la barre nomme le jour (DF-0273 : « demain »
                  ne suffit pas, il est ambigu apres minuit — et un J+3 ne doit
                  jamais se lire « demain »), sinon le seul endroit qui porte le
                  jour est l'onglet de la carte « Horaire », quittee depuis
                  longtemps. */}
              {asapActive ? `${t('checkout.slotAsap')} (${activeSlots[0]})` : t(
                checkout.mode === 'delivery'
                  ? (selectedDay >= 1 ? 'checkout.deliveryAtShortDay' : 'checkout.deliveryAtShort')
                  : (selectedDay >= 1 ? 'checkout.pickupAtShortDay' : 'checkout.pickupAtShort'),
                { time: selectedSlot, day: selectedDay >= 1 ? dayLabelForOffset(now, selectedDay, i18n.language, identity) : undefined }
              )}
            </span>
          )}
        </div>
      </div>

      {/* SC-587: the server refused named cart lines — clear them in one click
          and the customer can submit again without re-reading a toast. */}
      <UnavailableItemsModal
        open={rejectedLines.length > 0}
        blocked={rejectedLines}
        onRemoveAll={() => {
          dropBlockedLines(rejectedLines);
          setRejectedLines([]);
        }}
        onClose={() => setRejectedLines([])}
        canContinueAfterRemoval={false}
      />

      {serviceQuestion && (
        <Modal
          open={serviceChoiceOpen}
          onClose={() => setServiceChoiceOpen(false)}
          ariaLabel={t('serviceChoice.title')}
        >
          <div className="p-6 space-y-4">
            <h2 className="font-display text-2xl m-0">{t('serviceChoice.title')}</h2>
            <ServiceChoiceButtons
              services={serviceQuestion}
              onChoose={(choice) => void onSubmit(choice)}
              disabled={isLoading}
            />
          </div>
        </Modal>
      )}
    </div>
  );
}
